import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { AdaptiveDpr, OrbitControls } from '@react-three/drei';
import * as THREE from 'three';
import ScalableUnits from './ScalableUnits';
import { fetchGoogleSheetExactGrid } from '../../utils/sheetParser';
import { parseCSV, buildDoaAssetRows } from '../../utils/csvParser';
import { S3_TWIN_CONFIG } from '../../constants';

// ---------------------------------------------------------
// RACK SHEET CONFIG & MAPPINGS
// ---------------------------------------------------------
const RACK_SHEET_ID =
  S3_TWIN_CONFIG.RACK_SHEET_ID;

const SHEET_GID =
  S3_TWIN_CONFIG.RACK_ELEVATION_GID;

const DOA_SHEET_ID =
  S3_TWIN_CONFIG.DOA_SHEET_ID;

const DOA_REPORT_SHEET_NAME =
  S3_TWIN_CONFIG.DOA_SHEET_NAME;

const DOA_REPORT_SHEET_GID =
  S3_TWIN_CONFIG.DOA_GID;

const AUTO_REFRESH_INTERVAL =
  S3_TWIN_CONFIG.REFRESH_INTERVAL_MS;
const DOA_CRITERIA = [
  { label: 'PSU Status', patterns: [/^psu\s*status\b/i] },
  { label: 'Fan Operation', patterns: [/^fan\s*operation\b/i] },
  { label: 'Airflow', patterns: [/^airflow\b/i] },
  { label: 'LED Status', patterns: [/^led\s*status\b/i] }
];


function getSuName(rackNum) {
  if (rackNum <= 13) return 'SU 1';
  if (rackNum <= 26) return 'SU 2';
  if (rackNum <= 39) return 'SU 3';
  return 'SU 4';
}

function parseRackTitle(title, fallbackIndex) {
  const rawTitle = String(title || '').trim();

  const rackNumberMatch = rawTitle.match(/\brack\s*(\d+)\b/i);
  const fallbackRackNum = Number(fallbackIndex) + 1;

  const rackNum = rackNumberMatch
    ? Number(rackNumberMatch[1])
    : fallbackRackNum;

  const rackNameMatch = rawTitle.match(/S\d+-\d+[A-Z]-\d{2}-\d{2}/i);

  let rackName = '';

  if (rackNameMatch) {
    rackName = rackNameMatch[0].toUpperCase();
  } else {
    rackName = rawTitle
      .replace(/\brack\s*\d+\b/i, '')
      .replace(/\//g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  if (!rackName) {
    rackName = `Rack ${rackNum}`;
  }

  return {
    rackNum: rackNum >= 1 && rackNum <= 52 ? rackNum : null,
    rackName,
    fullTitle: rawTitle || `Rack ${rackNum}`
  };
}


function normalizeProductKey(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/\([^)]*\)/g, '')
    .replace(/\bv\d+\b/g, '')
    .replace(/[^a-z0-9]+/g, '');
}

function hasValue(value) {
  const normalized = String(value || '').trim().toLowerCase();
  return Boolean(normalized && !['-', 'na', 'n/a', 'null', 'undefined', 'unknown'].includes(normalized));
}

function findObjectHeader(headers, patterns) {
  return headers.find((header) => patterns.some((pattern) => pattern.test(String(header || ''))));
}

function looksLikeSerial(value) {
  const text = String(value || '').trim();
  if (!hasValue(text)) return false;
  if (/^pass|fail|good|bad|ok$/i.test(text)) return false;
  return text.length >= 5 && (
    (/[a-z]/i.test(text) && /\d/.test(text)) ||
    /^\d{5,}$/.test(text)
  );
}

function parseInlineProductIds(value) {
  const text = String(value || '').replace(/^product\s*id\b/i, ' ').trim();
  const productPattern = /\b(?:HF-?6100-[A-Z0-9-]+|UCSC-[A-Z0-9-]+|UCS-FI-\d+|N9K-[A-Z0-9-]+|CSF-\d+|AP\d+)(?:\s+V\d+)?\b|UCS\s+X-\d+(?:\s+-\s+NOT\s+DOA\s+TESTED)?|UCS\s+X\d+c\s+M\d+\s+Blade\s+x\d+/gi;
  return Array.from(text.matchAll(productPattern), (match) => match[0].trim());
}

function parseInlineSerials(value) {
  return String(value || '')
    .replace(/^serial\s*no\b/i, ' ')
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(looksLikeSerial);
}

function parseDoaStatus(value) {
  const text = String(value || '').trim();
  if (/\bfail(?:ed)?\b/i.test(text)) return 'FAIL';
  if (/\bpass(?:ed)?\b/i.test(text)) return 'PASS';
  return '';
}

function parseInlineDoaStatuses(value) {
  return Array.from(
    String(value || '').matchAll(/\b(pass|fail(?:ed)?)\b/gi),
    (match) => (/^fail/i.test(match[1]) ? 'FAIL' : 'PASS')
  );
}

function getDoaAssetSerial(asset) {
  return String(
    typeof asset === 'string'
      ? asset
      : asset?.serialNo || ''
  ).trim();
}

function normalizeSerialKey(value) {
  return String(value || '').trim().toLowerCase();
}

function addSerialToQueues(
  queues,
  productId,
  serialNo,
  failedCriteria = [],
  isTested = false
) {
  if (!hasValue(productId) || !looksLikeSerial(serialNo)) {
    return;
  }

  const cleanedSerial = String(serialNo).trim();

  const cleanedFailures = Array.from(
    new Set(failedCriteria.filter(Boolean))
  );

  getProductAliases(productId).forEach((key) => {
    if (!queues.has(key)) {
      queues.set(key, []);
    }

    const queue = queues.get(key);

    const existing = queue.find(
      (asset) =>
        normalizeSerialKey(getDoaAssetSerial(asset)) ===
        normalizeSerialKey(cleanedSerial)
    );

    if (existing && typeof existing === 'object') {
      existing.failedCriteria = Array.from(
        new Set([
          ...(existing.failedCriteria || []),
          ...cleanedFailures,
        ])
      );

      existing.hasFailure =
        existing.failedCriteria.length > 0;

      // Once tested, always keep it tested.
      existing.isTested =
        Boolean(existing.isTested || isTested);

      return;
    }

    queue.push({
      productId: String(productId).trim(),
      serialNo: cleanedSerial,

      // PASS or FAIL criteria completed
      isTested: Boolean(isTested),

      hasFailure:
        cleanedFailures.length > 0,

      failedCriteria:
        cleanedFailures,
    });
  });
}

function addInlineDoaSerials(queues, rows) {
  rows.forEach((row) => {
    const productCell = row.find((cell) => /^product\s*id\b/i.test(String(cell || '').trim()));
    const serialCell = row.find((cell) => /^serial\s*no\b/i.test(String(cell || '').trim()));

    if (!productCell || !serialCell) return;

    const products = parseInlineProductIds(productCell);
    const serials = parseInlineSerials(serialCell);
    const pairCount = Math.min(products.length, serials.length);
    const criterionStatuses = DOA_CRITERIA.map(({ label, patterns }) => {
      const statusCell = row.find((cell) =>
        patterns.some((pattern) => pattern.test(String(cell || '').trim()))
      );

      return {
        label,
        statuses: parseInlineDoaStatuses(statusCell)
      };
    });

    for (let index = 0; index < pairCount; index++) {
      const failedCriteria = criterionStatuses
        .filter(({ statuses }) => statuses[index] === 'FAIL')
        .map(({ label }) => label);

      addSerialToQueues(queues, products[index], serials[index], failedCriteria);
    }
  });
}

function getProductAliases(value) {
  const text = String(value || '').trim();
  const noParentheses = text.replace(/\([^)]*\)/g, '').trim();
  const beforeDashDescription = noParentheses.split(/\s+-\s+|\s+–\s+|\s+—\s+/)[0]?.trim();
  const withoutRevision = noParentheses.replace(/\bv\d+\b/ig, '').trim();

  return Array.from(new Set([text, noParentheses, beforeDashDescription, withoutRevision]
    .map(normalizeProductKey)
    .filter((key) => key.length >= 4)));
}

async function fetchGoogleSheetNamedCsv(sheetId, sheetName, sheetGid = '') {
  const encodedName = encodeURIComponent(sheetName);
  const cacheBuster = Date.now();
  const urls = [
    ...(sheetGid
      ? [`https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv&gid=${sheetGid}&t=${cacheBuster}`]
      : []),
    `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=out:csv&sheet=${encodedName}&t=${cacheBuster}`,
    `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv&sheet=${encodedName}&t=${cacheBuster}`
  ];

  let lastError = null;
  for (const url of urls) {
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Sheet tab fetch failed: ${response.status}`);
      return response.text();
    } catch (err) {
      lastError = err;
    }
  }

  throw lastError || new Error(`Could not fetch sheet tab: ${sheetName}`);
}

function buildDoaSerialQueues(csvText = '') {
  const rows = parseCSV(csvText);
  if (!rows.length) return new Map();

  const queues = new Map();
  addInlineDoaSerials(queues, rows);

  const headers = rows[0] || [];
  const assetRows = buildDoaAssetRows(rows, headers);
  const objectHeaders = assetRows.length ? Object.keys(assetRows[0]) : headers;
  const productHeader = findObjectHeader(objectHeaders, [
    /^product\s*id\b/i,
    /^pid$/i,
    /^product\s*code\b/i,
    /^product$/i
  ]) || findObjectHeader(objectHeaders, [/description/i]);
  const serialHeader = findObjectHeader(objectHeaders, [
    /serial\s*no/i,
    /serial\s*number/i,
    /^serial$/i,
    /s\/n/i
  ]);
  const criterionHeaders = DOA_CRITERIA.map(({ label, patterns }) => ({
    label,
    header: findObjectHeader(objectHeaders, patterns)
  }));

  if (!productHeader || !serialHeader) return queues;

  assetRows.forEach((row) => {
  const productId =
    row[productHeader];

  const serialNo =
    row[serialHeader];


  // Read all four DOA statuses.
  const doaResults =
    criterionHeaders.map(
      ({ label, header }) => ({
        label,

        status:
          header
            ? parseDoaStatus(row[header])
            : '',
      })
    );


  // A FAIL still means the device WAS tested.
  const failedCriteria =
    doaResults
      .filter(
        ({ status }) =>
          status === 'FAIL'
      )
      .map(
        ({ label }) =>
          label
      );


  /*
   * Device counts as DOA tested only when
   * all four criteria contain PASS or FAIL.
   *
   * Blank rows therefore do NOT increase
   * the progress percentage.
   */
  const isTested =
    doaResults.length > 0 &&
    doaResults.every(
      ({ status }) =>
        status === 'PASS' ||
        status === 'FAIL'
    );


  addSerialToQueues(
    queues,
    productId,
    serialNo,
    failedCriteria,
    isTested
  );
});

  return queues;
}

function cloneSerialQueues(queues) {
  return new Map(
    Array.from(queues || new Map(), ([key, values]) => [
      key,
      values.map((asset) =>
        typeof asset === 'object'
          ? {
              ...asset,
              failedCriteria: [...(asset.failedCriteria || [])]
            }
          : asset
      )
    ])
  );
}

function findDoaAssetBySerial(serialQueues, serialNo) {
  const serialKey = normalizeSerialKey(serialNo);
  if (!serialKey) return null;

  for (const queue of serialQueues.values()) {
    const asset = queue.find(
      (entry) => normalizeSerialKey(getDoaAssetSerial(entry)) === serialKey
    );
    if (asset) return asset;
  }

  return null;
}

function removeSerialFromAllQueues(serialQueues, serialNo) {
  const serialKey = normalizeSerialKey(serialNo);

  serialQueues.forEach((queue) => {
    const matchIndex = queue.findIndex(
      (asset) => normalizeSerialKey(getDoaAssetSerial(asset)) === serialKey
    );
    if (matchIndex >= 0) queue.splice(matchIndex, 1);
  });
}

function dequeueDoaAssetForProduct(serialQueues, productName) {
  if (!serialQueues?.size) return null;

  const aliases = getProductAliases(productName);
  for (const alias of aliases) {
    const queue = serialQueues.get(alias);
    if (queue?.length) {
      const asset = queue.shift();
      removeSerialFromAllQueues(serialQueues, getDoaAssetSerial(asset));
      return asset;
    }
  }

  for (const alias of aliases) {
    const looseMatch = Array.from(serialQueues.keys()).find((key) =>
      key.length >= 6 &&
      alias.length >= 6 &&
      (alias.includes(key) || key.includes(alias)) &&
      serialQueues.get(key)?.length
    );

    if (looseMatch) {
      const asset = serialQueues.get(looseMatch).shift();
      removeSerialFromAllQueues(serialQueues, getDoaAssetSerial(asset));
      return asset;
    }
  }

  return null;
}

function getDeviceSheetLabel(device = {}) {
  const directKeys = [
    'label',
    'Label',
    'slNo',
    'Sl No',
    'SL No',
    'serialNo',
    'serialNumber',
    'Serial Number',
    'Serial No.',
    'Serial No',
    'Serial / Label',
    'assetNo',
    'Asset No',
    'Asset ID',
    'AssetId',
    'barcode',
    'Barcode'
  ];

  for (const key of directKeys) {
    if (hasValue(device?.[key])) {
      return String(device[key]).trim();
    }
  }

  // Fallback: catch any unexpected serial/label column name from parser
  const dynamicKey = Object.keys(device || {}).find((key) =>
    /serial|label|sl\s*no|asset|barcode/i.test(key) && hasValue(device[key])
  );

  return dynamicKey ? String(device[dynamicKey]).trim() : '';
}

function mapRackElevationToTwinData(racks = [], doaSerialQueues = new Map()) {
  const twinData = {};
  const serialQueues = cloneSerialQueues(doaSerialQueues);

  for (let i = 1; i <= 52; i++) {
    twinData[i] = {
      id: i,
      name: `Rack ${i}`,
      displayName: `Rack ${i}`,
      sheetTitle: '',
      su: getSuName(i),
      total: 0,
      inspected: 0,
      percentage: 0,
      hasData: false,
      hasDoaFailure: false,
      doaFailures: [],
      devices: {}
    };
  }

  racks.forEach((rack, rackIndex) => {
    const rackTitle =
      rack?.title ||
      rack?.block?.title ||
      rack?.name ||
      rack?.rack ||
      rack?.Rack ||
      '';

    const parsedRack = parseRackTitle(rackTitle, rackIndex);
    const rackNum = parsedRack.rackNum;

    if (!rackNum || !twinData[rackNum]) return;

    const twinRack = twinData[rackNum];
    const devices = Array.isArray(rack.devices) ? rack.devices : [];

    twinRack.name = parsedRack.rackName;
    twinRack.displayName = `Rack ${rackNum} / ${parsedRack.rackName}`;
    twinRack.sheetTitle = parsedRack.fullTitle;
    twinRack.hasData = devices.length > 0;

    devices.forEach((device) => {
      const deviceName =
        device?.name ||
        device?.deviceName ||
        device?.description ||
        device?.['Device Name'] ||
        'Unknown Asset';

      const sheetSerial = getDeviceSheetLabel(device);
      const sheetDoaAsset = hasValue(sheetSerial)
        ? findDoaAssetBySerial(serialQueues, sheetSerial)
        : null;

      if (hasValue(sheetSerial)) {
        removeSerialFromAllQueues(serialQueues, sheetSerial);
      }

      const matchedDoaAsset = hasValue(sheetSerial)
        ? sheetDoaAsset
        : dequeueDoaAssetForProduct(serialQueues, deviceName);
      const doaSerial =
  getDoaAssetSerial(
    matchedDoaAsset
  );

const displayLabel =
  sheetSerial ||
  doaSerial ||
  '';


/*
 * DOA serial is only useful for
 * displaying a failure record.
 */
const doaDisplaySerial =
  sheetSerial ||
  doaSerial ||
  '';

      if (matchedDoaAsset?.hasFailure) {
  twinRack.hasDoaFailure = true;

  twinRack.doaFailures.push({
    productId: deviceName,
    serialNo: doaDisplaySerial,
    failedCriteria: matchedDoaAsset.failedCriteria || [],
  });
}
      const power =
        device?.power ||
        device?.Power ||
        device?.maxPower ||
        device?.watts ||
        device?.['Max Power (Watts)'] ||
        device?.['Max Power'] ||
        'N/A';

      let startU =
        device?.startU ||
        device?.u ||
        device?.U ||
        device?.uPosition ||
        '';

      if (typeof startU === 'string') {
        startU = startU.replace(/\D/g, '');
      }

      // A serial resolved from either sheet means the device is installed.
      const completed = hasValue(sheetSerial) || Boolean(matchedDoaAsset?.isTested);

      twinRack.total += 1;
      if (completed) twinRack.inspected += 1;

      if (!twinRack.devices[deviceName]) {
        twinRack.devices[deviceName] = {
          count: 0,
          inspected: 0,
          labels: [],
          uPositions: [],
          sizes: [],
          powerConfigs: [],
          installStates: []
        };
      }

      const rackDevice = twinRack.devices[deviceName];

      rackDevice.count += 1;
      if (completed) rackDevice.inspected += 1;

      if (startU) {
        rackDevice.labels.push(displayLabel ? String(displayLabel) : '');
        rackDevice.uPositions.push(Number(startU));
        rackDevice.sizes.push(Math.max(1, Math.min(45, Number(device?.size) || 1)));
        rackDevice.powerConfigs.push(power || 'N/A');
        rackDevice.installStates.push(completed);
      }
    });
  });

  Object.values(twinData).forEach((rack) => {
    if (rack.total > 0) {
      rack.percentage = Math.round((rack.inspected / rack.total) * 100);
    }
  });

  return twinData;
}

function isInstalledDeviceEntry(device = {}, index = 0) {
  if (Array.isArray(device.installStates) && index < device.installStates.length) {
    return Boolean(device.installStates[index]);
  }

  const label = String(device.labels?.[index] || '').trim();
  return hasValue(label) && !/^(pending|not installed|unassigned)$/i.test(label);
}

function normalizeRackCompletion(rack = {}) {
  let total = 0;
  let inspected = 0;

  Object.values(rack.devices || {}).forEach((device = {}) => {
    const declaredCount = Number(device.count);
    const detailCount = Math.max(
      device.labels?.length || 0,
      device.installStates?.length || 0,
      device.uPositions?.length || 0
    );
    const count = Math.max(Number.isFinite(declaredCount) ? declaredCount : 0, detailCount);
    const detailedInstalled = Array.from(
      { length: detailCount },
      (_, index) => isInstalledDeviceEntry(device, index)
    ).filter(Boolean).length;

    total += count;
    inspected += detailedInstalled;

    if (count > detailCount) {
      const declaredInspected = Number(device.inspected);
      if (Number.isFinite(declaredInspected)) {
        inspected += Math.min(
          count - detailCount,
          Math.max(0, declaredInspected - detailedInstalled)
        );
      }
    }
  });

  if (!total) {
    total = Math.max(0, Number(rack.total) || 0);
    inspected = Math.min(total, Math.max(0, Number(rack.inspected) || 0));
  }

  inspected = Math.min(total, inspected);

  return {
    ...rack,
    total,
    inspected,
    percentage: total > 0 ? Math.round((inspected / total) * 100) : 0
  };
}

function normalizeTwinCompletion(data = {}) {
  return Object.fromEntries(
    Object.entries(data).map(([rackNum, rack]) => [rackNum, normalizeRackCompletion(rack)])
  );
}

function getRackPopupSpan(device = {}) {
  const inferredSpan = Math.max(1, Math.min(45, Number(device.size) || 1));
  const name = String(device.name || '').toLowerCase();

  let physicalSpan = 1;

  if (/ucs\s*x[-\s]?9508/i.test(name)) {
    physicalSpan = 7;
  } else if (/hf[-\s]?6100[-\s]?64ed/i.test(name)) {
    physicalSpan = 2;
  } else if (/ucsc[-\s]?880a[-\s]?m8/i.test(name)) {
    physicalSpan = 2;
  } else if (/nvidia\s*sn5610/i.test(name)) {
    physicalSpan = 2;
  } else if (/ucs\s*fi[-\s]?6536/i.test(name)) {
    physicalSpan = 1;
  } else if (/hf[-\s]?6100[-\s]?60l4d/i.test(name)) {
    physicalSpan = 1;
  } else if (/hf[-\s]?6100[-\s]?32d/i.test(name)) {
    physicalSpan = 1;
  } else if (/n9k[-\s]?c?9348/i.test(name)) {
    physicalSpan = 1;
  } else if (/n9k[-\s]?c?93180/i.test(name)) {
    physicalSpan = 1;
  } else if (/ucs\s*c225m8/i.test(name)) {
    physicalSpan = 1;
  } else if (/poweredge\s*r670/i.test(name)) {
    physicalSpan = 1;
  } else if (/csf\s*1230|csf1230/i.test(name)) {
    physicalSpan = 1;
  } else if (/pathpanel|fobot|router/i.test(name)) {
    physicalSpan = 1;
  }

  return Math.max(1, Math.min(inferredSpan, physicalSpan));
}
// ---------------------------------------------------------
// RACK POSITION CALCULATOR 
// ---------------------------------------------------------
function getRackWorldPosition(rackNum) {
  const suIdx = Math.floor((rackNum - 1) / 13);
  const i = ((rackNum - 1) % 13) + 1;
  const SU_CENTERS = [-24, -8, 8, 24];
  const LEFT_ROW_Z = [16, 12, 8, 0, -4, -8];
  const RIGHT_ROW_Z = [16, 12, 8, 4, 0, -4, -8];

  const isLeftColumn = i <= 6;
  
  const suBaseX = SU_CENTERS[suIdx];
  const rackX = suBaseX + (isLeftColumn ? -3.6 : 3.6); 
  const rackZ = isLeftColumn
    ? LEFT_ROW_Z[i - 1]
    : RIGHT_ROW_Z[i - 7];

  return { rackX, rackZ, suBaseX, isLeftColumn };
}

// ---------------------------------------------------------
// CAMERA ANIMATORS
// ---------------------------------------------------------
// Straight centered default view.
// This matches the earlier preferred default view: centered, front-facing,
// slightly elevated, and not shifted to the right.
const DEFAULT_CAM_POS = new THREE.Vector3(0, 45, 60);
const DEFAULT_LOOK_AT = new THREE.Vector3(0, 0.02, 5);

const CAMERA_LIMITS = {
  minY: 0.65,
  maxY: 82,
  minTargetY: 0.02,
  maxTargetY: 82,
  minX: -105,
  maxX: 105,
  minZ: -60,
  maxZ: 70
};

function clampFreeNavigation(camera, controls) {
  if (!camera || !controls) return;
  camera.position.x = THREE.MathUtils.clamp(camera.position.x, CAMERA_LIMITS.minX, CAMERA_LIMITS.maxX);
  camera.position.y = THREE.MathUtils.clamp(camera.position.y, CAMERA_LIMITS.minY, CAMERA_LIMITS.maxY);
  camera.position.z = THREE.MathUtils.clamp(camera.position.z, CAMERA_LIMITS.minZ, CAMERA_LIMITS.maxZ);
  controls.target.x = THREE.MathUtils.clamp(controls.target.x, CAMERA_LIMITS.minX, CAMERA_LIMITS.maxX);
  controls.target.y = THREE.MathUtils.clamp(controls.target.y, CAMERA_LIMITS.minTargetY, CAMERA_LIMITS.maxTargetY);
  controls.target.z = THREE.MathUtils.clamp(controls.target.z, CAMERA_LIMITS.minZ, CAMERA_LIMITS.maxZ);
  controls.update?.();
}

function CameraAnimator({
  controlsRef,
  selectedRackNum,
  isTouring,
  suppressDefaultCamera = false,
  normalViewRequestId = 0
}) {
  const [animTarget, setAnimTarget] = useState(null);
  const lastNormalViewRequestId = useRef(0);

  useEffect(() => {
    if (isTouring) {
      setAnimTarget(null);
      return;
    }

    // Explicit return-to-home request gets priority.
    // This lets the rack popup fade out while the camera already glides back home.
    if (normalViewRequestId > 0 && lastNormalViewRequestId.current !== normalViewRequestId) {
      lastNormalViewRequestId.current = normalViewRequestId;
      setAnimTarget({
        camPos: DEFAULT_CAM_POS.clone(),
        lookAt: DEFAULT_LOOK_AT.clone(),
        mode: 'defaultReturn'
      });
      return;
    }

    if (selectedRackNum) {
      const { rackX, rackZ, isLeftColumn } = getRackWorldPosition(selectedRackNum);

      // Move to the physical front side of the rack.
      // In ScalableUnits, left-column front faces are on -X,
      // right-column front faces are on +X.
      const frontOffsetX = isLeftColumn ? -6.2 : 6.2;

      setAnimTarget({
        camPos: new THREE.Vector3(rackX + frontOffsetX, 6.2, rackZ + 0.2),
        lookAt: new THREE.Vector3(rackX, 5.8, rackZ),
        mode: 'rackOpen'
      });

      return;
    }

    if (suppressDefaultCamera) {
      setAnimTarget(null);
      return;
    }

    setAnimTarget(null);
  }, [selectedRackNum, isTouring, suppressDefaultCamera, normalViewRequestId]);

  useFrame((state, delta) => {
    if (!controlsRef.current || !animTarget || isTouring) return;

    state.camera.up.set(0, 1, 0);

    const speed = animTarget.mode === 'defaultReturn' ? 5.2 : 4;
    const alpha = 1 - Math.exp(-speed * delta);

    state.camera.position.lerp(animTarget.camPos, alpha);
    controlsRef.current.target.lerp(animTarget.lookAt, alpha);
    state.camera.lookAt(controlsRef.current.target);
    controlsRef.current.update?.();

    const cameraDone = state.camera.position.distanceTo(animTarget.camPos) < 0.28;
    const targetDone = controlsRef.current.target.distanceTo(animTarget.lookAt) < 0.18;

    if (cameraDone && targetDone) {
      state.camera.position.copy(animTarget.camPos);
      controlsRef.current.target.copy(animTarget.lookAt);
      state.camera.lookAt(animTarget.lookAt);
      controlsRef.current.update?.();

      setAnimTarget(null);
    }
  });

  return null;
}



// ---------------------------------------------------------
// TOP VIEW CAMERA ANIMATOR
// Moves camera to a clean top-down overview.
// ---------------------------------------------------------
function TopViewAnimator({
  controlsRef,
  requestId,
  isBlocked,
  onStart,
  onDone
}) {
  const lastRequestId = useRef(0);
  const isAnimating = useRef(false);
  const progress = useRef(0);

  const startCamPos = useRef(new THREE.Vector3());
  const startTarget = useRef(new THREE.Vector3());

  // 2D-ish top map view.
  // Camera is centered over the whole twin so roads and racks are visible equally.
  // Tiny X/Z offset avoids the exact vertical singularity while still looking top-down.
  const TOP_CAM_POS = useMemo(() => new THREE.Vector3(0.01, 82, 4.01), []);
  const TOP_LOOK_AT = useMemo(() => new THREE.Vector3(0, 0.02, 4), []);

  useFrame((state, delta) => {
    if (!controlsRef.current || isBlocked) return;

    if (requestId > 0 && lastRequestId.current !== requestId) {
      lastRequestId.current = requestId;
      isAnimating.current = true;
      progress.current = 0;

      startCamPos.current.copy(state.camera.position);
      startTarget.current.copy(controlsRef.current.target);

      onStart?.();
    }

    if (!isAnimating.current) return;

    progress.current = Math.min(1, progress.current + delta * 1.35);

    const eased = 1 - Math.pow(1 - progress.current, 4);

    // Use Z as the screen-up direction for a clean floor-plan/top-map look.
    state.camera.up.set(0, 0, -1);
    state.camera.position.copy(startCamPos.current).lerp(TOP_CAM_POS, eased);
    controlsRef.current.target.copy(startTarget.current).lerp(TOP_LOOK_AT, eased);

    state.camera.lookAt(controlsRef.current.target);

    // Do not call controls.update() here. It can fight the camera animation.
    if (progress.current >= 1) {
      isAnimating.current = false;
      onDone?.();
    }
  });

  return null;
}
// ---------------------------------------------------------
// STRICT CXO TOUR - STABLE VERSION WITH FIXED RIGHT TURNS
// Keeps old movement style.
// Fixes only camera heading so left/right turns behave the same.
// ---------------------------------------------------------
function TourAnimator({ controlsRef, isTouring, onDoorReady }) {
  const tourPath = useMemo(() => {
    const Y = 5.7;

    const cx = [-32, -16, 0, 16, 32];

    const frontZ = 20.5;
    const rearZ = -12;
    const doorZ = frontZ;

    const leftOutsideDoorX = -58;
    const leftInsideDoorX = -50.2;
    const leftEntryX = -50;

    const rightEntryX = 50;
    const rightInsideDoorX = 50.2;
    const rightOutsideDoorX = 58;

    // Walkable rear-corridor U-turn pocket.
    // These points stay inside the rear blue pathway and rightmost aisle.
    const uTurnX = cx[4];
    const uTurnInsideX = cx[4] - 2.9;
    const uTurnInsideZ = rearZ + 2.65;

    const rawPts = [
      new THREE.Vector3(leftOutsideDoorX, Y, doorZ),
      new THREE.Vector3(leftInsideDoorX, Y, doorZ),
      new THREE.Vector3(leftEntryX, Y, doorZ),
      new THREE.Vector3(cx[0], Y, frontZ),
      new THREE.Vector3(cx[0], Y, rearZ),
      new THREE.Vector3(cx[1], Y, rearZ),
      new THREE.Vector3(cx[1], Y, frontZ),
      new THREE.Vector3(cx[2], Y, frontZ),
      new THREE.Vector3(cx[2], Y, rearZ),
      new THREE.Vector3(cx[3], Y, rearZ),
      new THREE.Vector3(cx[3], Y, frontZ),
      new THREE.Vector3(cx[4], Y, frontZ),
      new THREE.Vector3(cx[4], Y, rearZ),
      new THREE.Vector3(uTurnInsideX, Y, rearZ),
      new THREE.Vector3(uTurnInsideX, Y, uTurnInsideZ),
      new THREE.Vector3(uTurnX, Y, uTurnInsideZ),
      new THREE.Vector3(cx[4], Y, frontZ),
      new THREE.Vector3(rightEntryX, Y, doorZ),
      new THREE.Vector3(rightInsideDoorX, Y, doorZ),
      new THREE.Vector3(rightOutsideDoorX, Y, doorZ)
    ];

    const TURN_RADIUS = 1.15;

    const parts = [];
    let currentStart = rawPts[0].clone();

    for (let i = 1; i < rawPts.length - 1; i++) {
      const prev = rawPts[i - 1];
      const corner = rawPts[i];
      const next = rawPts[i + 1];

      const inVec = corner.clone().sub(prev);
      const outVec = next.clone().sub(corner);

      const inLen = inVec.length();
      const outLen = outVec.length();

      if (inLen < 0.001 || outLen < 0.001) continue;

      const inDir = inVec.clone().normalize();
      const outDir = outVec.clone().normalize();

      const dot = inDir.dot(outDir);

      if (dot > 0.985) {
        continue;
      }

      const trim = Math.min(TURN_RADIUS, inLen * 0.32, outLen * 0.32);

      const curveStart = corner.clone().sub(inDir.clone().multiplyScalar(trim));
      const curveEnd = corner.clone().add(outDir.clone().multiplyScalar(trim));

      if (currentStart.distanceTo(curveStart) > 0.001) {
        const line = new THREE.LineCurve3(currentStart.clone(), curveStart.clone());
        parts.push({ curve: line, length: line.getLength() });
      }

      const curve = new THREE.QuadraticBezierCurve3(curveStart.clone(), corner.clone(), curveEnd.clone());
      parts.push({ curve, length: curve.getLength() });
      currentStart = curveEnd.clone();
    }

    const last = rawPts[rawPts.length - 1].clone();

    if (currentStart.distanceTo(last) > 0.001) {
      const finalLine = new THREE.LineCurve3(currentStart.clone(), last.clone());
      parts.push({ curve: finalLine, length: finalLine.getLength() });
    }

    const totalLength = parts.reduce((sum, part) => sum + part.length, 0);

    const sample = (distance) => {
      let remaining = THREE.MathUtils.clamp(distance, 0, totalLength);

      for (const part of parts) {
        if (remaining <= part.length) {
          const t = part.length === 0 ? 0 : remaining / part.length;
          return part.curve.getPoint(t);
        }
        remaining -= part.length;
      }

      return last.clone();
    };

    return { sample, totalLength };
  }, []);

  const distanceTravelled = useRef(0);
  const startDelay = useRef(0);
  const transitionProgress = useRef(0);
  const startCamPos = useRef(new THREE.Vector3());
  const startTarget = useRef(new THREE.Vector3());
  const doorReady = useRef(false);
  const wasTouring = useRef(false);
  const tourFinished = useRef(false);

  useFrame((state, delta) => {
    if (!isTouring || !controlsRef.current) {
      if (!isTouring) {
        distanceTravelled.current = 0;
        startDelay.current = 0;
        transitionProgress.current = 0;
        tourFinished.current = false;

        if (doorReady.current || wasTouring.current) {
          doorReady.current = false;
          onDoorReady?.(false);
        }

        wasTouring.current = false;
      }
      return;
    }

    if (!wasTouring.current) {
      distanceTravelled.current = 0;
      startDelay.current = 0.55;
      transitionProgress.current = 0;
      tourFinished.current = false;
      startCamPos.current.copy(state.camera.position);
      startTarget.current.copy(controlsRef.current.target);
      doorReady.current = false;
      onDoorReady?.(false);
      wasTouring.current = true;
    }

    const entryCamPos = tourPath.sample(0);
    const entryLookTarget = tourPath.sample(3.5);
    entryLookTarget.y = 5.7;

    if (transitionProgress.current < 1) {
      transitionProgress.current = Math.min(1, transitionProgress.current + delta * 0.75);
      const eased = 1 - Math.pow(1 - transitionProgress.current, 3);
      state.camera.position.copy(startCamPos.current).lerp(entryCamPos, eased);
      controlsRef.current.target.copy(startTarget.current).lerp(entryLookTarget, eased);
      state.camera.up.set(0, 1, 0);
      state.camera.lookAt(controlsRef.current.target);
      if (transitionProgress.current >= 1 && !doorReady.current) {
        doorReady.current = true;
        onDoorReady?.(true);
      }
      return;
    }

    if (!doorReady.current) {
      doorReady.current = true;
      onDoorReady?.(true);
    }

    if (startDelay.current > 0) {
      startDelay.current = Math.max(0, startDelay.current - delta);
    } else if (!tourFinished.current) {
      distanceTravelled.current = Math.min(tourPath.totalLength, distanceTravelled.current + delta * 4.2);
    }

    const camPos = tourPath.sample(distanceTravelled.current);
    const lookTarget = tourPath.sample(Math.min(distanceTravelled.current + 3.5, tourPath.totalLength));
    lookTarget.y = 5.7;

    state.camera.position.copy(camPos);
    controlsRef.current.target.copy(lookTarget);
    state.camera.up.set(0, 1, 0);
    state.camera.lookAt(lookTarget);

    if (distanceTravelled.current >= tourPath.totalLength && !tourFinished.current) {
      tourFinished.current = true;
      setTimeout(() => {
        doorReady.current = false;
        onDoorReady?.(false);
      }, 900);
    }
  });

  return null;
}
// ---------------------------------tgyugbiojhuifyuhui------------------------
// BUTTERY SMOOTH MANUAL DRONE CONTROLLER
// ---------------------------------------------------------
function DroneControls({ controlsRef, isTouring, isLocked }) {
  const keys = useRef({
    w: false,
    a: false,
    s: false,
    d: false,
    q: false,
    e: false,
    shift: false
  });

  useEffect(() => {
    const movementKeys = new Set(['w', 'a', 's', 'd', 'q', 'e', 'arrowup', 'arrowleft', 'arrowdown', 'arrowright']);

    const handleKeyDown = (ev) => {
      if (document.activeElement?.tagName === 'INPUT') return;
      const key = ev.key.toLowerCase();
      if (movementKeys.has(key)) ev.preventDefault();
      if (key === 'w' || ev.key === 'ArrowUp') keys.current.w = true;
      if (key === 'a' || ev.key === 'ArrowLeft') keys.current.a = true;
      if (key === 's' || ev.key === 'ArrowDown') keys.current.s = true;
      if (key === 'd' || ev.key === 'ArrowRight') keys.current.d = true;
      if (key === 'e') keys.current.e = true;
      if (key === 'q') keys.current.q = true;
      if (ev.shiftKey) keys.current.shift = true;
    };

    const handleKeyUp = (ev) => {
      const key = ev.key.toLowerCase();
      if (movementKeys.has(key)) ev.preventDefault();
      if (key === 'w' || ev.key === 'ArrowUp') keys.current.w = false;
      if (key === 'a' || ev.key === 'ArrowLeft') keys.current.a = false;
      if (key === 's' || ev.key === 'ArrowDown') keys.current.s = false;
      if (key === 'd' || ev.key === 'ArrowRight') keys.current.d = false;
      if (key === 'e') keys.current.e = false;
      if (key === 'q') keys.current.q = false;
      if (!ev.shiftKey) keys.current.shift = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  useFrame((state, delta) => {
    if (!controlsRef.current || isTouring || isLocked) return;

    const speed = keys.current.shift ? 40 * delta : 18 * delta;
    const verticalSpeed = keys.current.shift ? 32 * delta : 14 * delta;

    const forward = new THREE.Vector3();
    state.camera.getWorldDirection(forward);
    forward.y = 0;
    forward.normalize();

    const right = new THREE.Vector3();
    right.crossVectors(state.camera.up, forward).normalize();

    const moveVector = new THREE.Vector3();
    if (keys.current.w) moveVector.addScaledVector(forward, speed);
    if (keys.current.s) moveVector.addScaledVector(forward, -speed);
    if (keys.current.a) moveVector.addScaledVector(right, speed);
    if (keys.current.d) moveVector.addScaledVector(right, -speed);

    const verticalMove = (keys.current.e ? verticalSpeed : 0) - (keys.current.q ? verticalSpeed : 0);

    if (moveVector.lengthSq() > 0) {
      const oldTarget = controlsRef.current.target.clone();
      controlsRef.current.target.add(moveVector);
      controlsRef.current.target.x = THREE.MathUtils.clamp(controlsRef.current.target.x, CAMERA_LIMITS.minX, CAMERA_LIMITS.maxX);
      controlsRef.current.target.z = THREE.MathUtils.clamp(controlsRef.current.target.z, CAMERA_LIMITS.minZ, CAMERA_LIMITS.maxZ);
      const actualMove = controlsRef.current.target.clone().sub(oldTarget);
      actualMove.y = 0;
      state.camera.position.add(actualMove);
    }

    if (verticalMove !== 0) {
      const oldCameraY = state.camera.position.y;
      const newCameraY = THREE.MathUtils.clamp(oldCameraY + verticalMove, CAMERA_LIMITS.minY, CAMERA_LIMITS.maxY);
      const actualVerticalMove = newCameraY - oldCameraY;
      state.camera.position.y += actualVerticalMove;
      controlsRef.current.target.y += actualVerticalMove;
      controlsRef.current.target.y = THREE.MathUtils.clamp(controlsRef.current.target.y, CAMERA_LIMITS.minTargetY, CAMERA_LIMITS.maxTargetY);
    }

    if (moveVector.lengthSq() > 0 || verticalMove !== 0) {
      clampFreeNavigation(state.camera, controlsRef.current);
    }
  });

  return null;
}

function CameraBounds({ controlsRef, isTouring }) {
  useFrame((state) => {
    if (!controlsRef.current || isTouring) return;
    clampFreeNavigation(state.camera, controlsRef.current);
  });
  return null;
}

function IntroCameraAnimator({ controlsRef, introPhase }) {
  const progress = useRef(0);
  const wasActive = useRef(false);
  const startCamPos = useMemo(() => new THREE.Vector3(0, 132, 22), []);
  const startLookAt = useMemo(() => new THREE.Vector3(0, 0, 7), []);
  const endCamPos = useMemo(() => DEFAULT_CAM_POS.clone(), []);
  const endLookAt = useMemo(() => DEFAULT_LOOK_AT.clone(), []);

  useFrame((state, delta) => {
    if (!controlsRef.current || introPhase === 'done') {
      if (controlsRef.current && introPhase === 'done' && wasActive.current) {
        state.camera.position.copy(endCamPos);
        controlsRef.current.target.copy(endLookAt);
        state.camera.lookAt(endLookAt);
        controlsRef.current.update?.();
      }
      wasActive.current = false;
      return;
    }

    if (!wasActive.current) {
      progress.current = 0;
      state.camera.position.copy(startCamPos);
      controlsRef.current.target.copy(startLookAt);
      state.camera.lookAt(startLookAt);
      controlsRef.current.update?.();
      wasActive.current = true;
    }

    if (introPhase === 'leaving') {
      progress.current = Math.min(1, progress.current + delta * 0.72);
    }

    const eased = 1 - Math.pow(1 - progress.current, 4);
    const arcLift = Math.sin(eased * Math.PI) * 8;
    const camPos = startCamPos.clone().lerp(endCamPos, eased);
    const lookAt = startLookAt.clone().lerp(endLookAt, eased);

    camPos.y += arcLift;
    state.camera.position.copy(camPos);
    controlsRef.current.target.copy(lookAt);
    state.camera.lookAt(lookAt);
    controlsRef.current.update?.();
  });

  return null;
}

// ---------------------------------------------------------
// INTRO EXPERIENCE
// ---------------------------------------------------------
function IntroAnimation({ isDark, onPhaseChange }) {
  const [phase, setPhase] = useState('enter');

  useEffect(() => {
    onPhaseChange?.('enter');
    const leaveTimer = setTimeout(() => {
      setPhase('leaving');
      onPhaseChange?.('leaving');
    }, 1950);
    const doneTimer = setTimeout(() => {
      setPhase('done');
      onPhaseChange?.('done');
    }, 3450);
    return () => {
      clearTimeout(leaveTimer);
      clearTimeout(doneTimer);
    };
  }, [onPhaseChange]);

  if (phase === 'done') return null;

  const isLeaving = phase === 'leaving';
  const introBg = isDark
    ? 'radial-gradient(circle at 50% 40%, rgba(45, 212, 191, 0.22), transparent 30%), radial-gradient(ellipse at 50% 72%, rgba(8, 145, 178, 0.22), transparent 48%), linear-gradient(135deg, #020b12 0%, #06202a 52%, #020617 100%)'
    : 'radial-gradient(circle at 50% 42%, rgba(14, 165, 233, 0.22), transparent 34%), linear-gradient(135deg, #eef7ff 0%, #ffffff 48%, #dbeafe 100%)';

  return (
    <div
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        background: introBg,
        opacity: isLeaving ? 0 : 1,
        transform: isLeaving ? 'scale(1.14)' : 'scale(1)',
        transition: 'opacity 1.15s cubic-bezier(0.16, 1, 0.3, 1), transform 1.25s cubic-bezier(0.16, 1, 0.3, 1)',
        pointerEvents: 'auto'
      }}
    >
      <style>{`
        @keyframes dcimRackRise {
          0% { opacity: 0; transform: translateY(26px) scale(0.92) rotateX(12deg); filter: blur(8px); }
          58% { opacity: 1; filter: blur(0); }
          100% { opacity: 1; transform: translateY(0) scale(1) rotateX(0deg); filter: blur(0); }
        }
        @keyframes dcimLogoFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes dcimLogoGlow {
          0%, 100% { opacity: 0.72; transform: scale(0.98); }
          50% { opacity: 1; transform: scale(1.04); }
        }
        @keyframes dcimUnitPulse {
          0%, 100% { opacity: 0.72; }
          50% { opacity: 1; }
        }
        @keyframes dcimLedPulse {
          0%, 100% { opacity: 0.35; }
          50% { opacity: 1; }
        }
        @keyframes dcimTitleIn {
          0% { opacity: 0; transform: translateY(18px); filter: blur(8px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0); }
        }
        @keyframes dcimLineLoad {
          0% { transform: scaleX(0); opacity: 0; }
          22% { opacity: 1; }
          100% { transform: scaleX(1); opacity: 1; }
        }
        @keyframes dcimSweep {
          0% { transform: translateX(-120px) rotate(18deg); opacity: 0; }
          30% { opacity: 0.85; }
          100% { transform: translateX(190px) rotate(18deg); opacity: 0; }
        }
        @keyframes dcimGridDrift {
          0% { transform: perspective(760px) rotateX(66deg) translateY(0); }
          100% { transform: perspective(760px) rotateX(66deg) translateY(42px); }
        }
        @keyframes dcimLightSweep {
          0% { transform: translateX(-42vw); opacity: 0; }
          30% { opacity: 0.75; }
          100% { transform: translateX(42vw); opacity: 0; }
        }
        .dcim-intro-logo {
          animation: dcimRackRise 0.9s cubic-bezier(0.16, 1, 0.3, 1) both, dcimLogoFloat 2.4s ease-in-out 0.95s infinite;
          transform-origin: center;
        }
        .dcim-intro-title {
          animation: dcimTitleIn 0.78s cubic-bezier(0.16, 1, 0.3, 1) 0.42s both;
          font-family: "Manrope", "Aptos Display", "Aptos Rounded", "Segoe UI Variable Display", "Nunito Sans", "Inter", system-ui, sans-serif;
        }
        .dcim-intro-line {
          animation: dcimLineLoad 1.08s cubic-bezier(0.16, 1, 0.3, 1) 0.88s both;
          transform-origin: center;
        }
        .dcim-intro-unit {
          animation: dcimUnitPulse 1.6s ease-in-out infinite;
        }
        .dcim-intro-led {
          animation: dcimLedPulse 1.15s ease-in-out infinite;
        }
        .dcim-intro-sweep {
          animation: dcimSweep 1.7s cubic-bezier(0.16, 1, 0.3, 1) 0.64s both;
        }
        .dcim-intro-grid {
          animation: dcimGridDrift 3.2s linear infinite;
          transform-origin: center top;
        }
        .dcim-intro-room-sweep {
          animation: dcimLightSweep 2.2s cubic-bezier(0.16, 1, 0.3, 1) 0.34s infinite;
        }
        .dcim-intro-aura {
          animation: dcimLogoGlow 2.2s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .dcim-intro-logo,
          .dcim-intro-title,
          .dcim-intro-line,
          .dcim-intro-unit,
          .dcim-intro-led,
          .dcim-intro-sweep,
          .dcim-intro-grid,
          .dcim-intro-room-sweep,
          .dcim-intro-aura {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>

      <div
        className="dcim-intro-grid"
        style={{
          position: 'absolute',
          left: '-18%',
          right: '-18%',
          bottom: '-24%',
          height: '64%',
          backgroundImage: isDark
            ? 'linear-gradient(rgba(45,212,191,0.16) 1px, transparent 1px), linear-gradient(90deg, rgba(45,212,191,0.14) 1px, transparent 1px)'
            : 'linear-gradient(rgba(14,165,233,0.14) 1px, transparent 1px), linear-gradient(90deg, rgba(14,165,233,0.12) 1px, transparent 1px)',
          backgroundSize: '44px 44px',
          opacity: isDark ? 0.32 : 0.24,
          WebkitMaskImage: 'linear-gradient(to bottom, transparent, #000 20%, #000 62%, transparent)',
          maskImage: 'linear-gradient(to bottom, transparent, #000 20%, #000 62%, transparent)',
          pointerEvents: 'none'
        }}
      />

      <div
        className="dcim-intro-room-sweep"
        style={{
          position: 'absolute',
          top: '24%',
          width: '34vw',
          height: 2,
          borderRadius: 999,
          background: 'linear-gradient(90deg, transparent, rgba(103,232,249,0.85), transparent)',
          boxShadow: '0 0 28px rgba(34,211,238,0.52)',
          pointerEvents: 'none'
        }}
      />

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 22,
          opacity: isLeaving ? 0 : 1,
          transform: isLeaving ? 'translateY(-4px) scale(2.15)' : 'translateY(0) scale(1)',
          transformOrigin: 'center center',
          transition: 'opacity 1.05s cubic-bezier(0.16, 1, 0.3, 1), transform 1.35s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
      >
        <div
          className="dcim-intro-logo"
          style={{
            width: 156,
            height: 156,
            display: 'grid',
            placeItems: 'center',
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 28,
            background: isDark
              ? 'linear-gradient(145deg, rgba(8, 47, 73, 0.56), rgba(2, 6, 23, 0.78))'
              : 'rgba(255, 255, 255, 0.72)',
            border: isDark ? '1px solid rgba(103, 232, 249, 0.34)' : '1px solid rgba(14, 116, 144, 0.18)',
            boxShadow: isDark ? '0 30px 92px rgba(0, 0, 0, 0.58), 0 0 54px rgba(34, 211, 238, 0.26), inset 0 1px 0 rgba(255,255,255,0.14)' : '0 28px 90px rgba(14, 116, 144, 0.18), 0 0 44px rgba(14, 165, 233, 0.2)',
            backdropFilter: 'blur(22px) saturate(1.45)',
            WebkitBackdropFilter: 'blur(22px) saturate(1.45)'
          }}
        >
          <div
            className="dcim-intro-aura"
            style={{
              position: 'absolute',
              width: 178,
              height: 178,
              borderRadius: 36,
              background: 'radial-gradient(circle, rgba(45,212,191,0.24), transparent 62%)',
              filter: 'blur(5px)'
            }}
          />
          <svg
            width="112"
            height="128"
            viewBox="0 0 112 128"
            role="img"
            aria-label="Server rack logo"
            style={{ position: 'relative', zIndex: 1 }}
          >
            <defs>
              <linearGradient id="dcimRackFace" x1="16" y1="4" x2="92" y2="122" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#d8fbff" />
                <stop offset="0.48" stopColor="#4f6f7a" />
                <stop offset="1" stopColor="#0a141d" />
              </linearGradient>
              <linearGradient id="dcimRackSide" x1="88" y1="10" x2="106" y2="116" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#1f4448" />
                <stop offset="1" stopColor="#020617" />
              </linearGradient>
              <linearGradient id="dcimGlass" x1="24" y1="8" x2="72" y2="118" gradientUnits="userSpaceOnUse">
                <stop offset="0" stopColor="#ffffff" stopOpacity="0.55" />
                <stop offset="0.36" stopColor="#2dd4bf" stopOpacity="0.16" />
                <stop offset="1" stopColor="#020617" stopOpacity="0.1" />
              </linearGradient>
              <filter id="dcimRackShadow" x="-25%" y="-20%" width="150%" height="160%">
                <feDropShadow dx="0" dy="14" stdDeviation="10" floodColor="#020617" floodOpacity="0.34" />
              </filter>
            </defs>

            <g filter="url(#dcimRackShadow)">
              <path d="M18 13 L79 4 L99 16 L99 116 L78 124 L18 113 Z" fill="url(#dcimRackFace)" />
              <path d="M79 4 L99 16 L99 116 L78 124 L78 17 Z" fill="url(#dcimRackSide)" opacity="0.95" />
              <path d="M24 20 L72 14 L72 108 L24 103 Z" fill="#0f172a" stroke="#94a3b8" strokeOpacity="0.55" strokeWidth="1.4" />

              {[0, 1, 2, 3, 4, 5].map((unit) => (
                <g key={unit} className="dcim-intro-unit" style={{ animationDelay: `${unit * 0.11}s` }}>
                  <path
                    d={`M30 ${27 + unit * 13} L67 ${23 + unit * 13} L67 ${31 + unit * 13} L30 ${35 + unit * 13} Z`}
                    fill={unit % 2 === 0 ? '#1e293b' : '#334155'}
                    stroke="#64748b"
                    strokeOpacity="0.55"
                  />
                  <circle
                    className="dcim-intro-led"
                    style={{ animationDelay: `${0.22 + unit * 0.13}s` }}
                    cx="36"
                    cy={31 + unit * 13}
                    r="1.7"
                    fill={unit % 3 === 0 ? '#2dd4bf' : '#67e8f9'}
                  />
                  <path
                    d={`M44 ${30 + unit * 13} L62 ${28 + unit * 13}`}
                    stroke="#94a3b8"
                    strokeOpacity="0.62"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                  />
                </g>
              ))}

              <path d="M24 20 L72 14 L72 108 L24 103 Z" fill="url(#dcimGlass)" />
              <path className="dcim-intro-sweep" d="M30 18 L42 16 L72 105 L60 108 Z" fill="#ffffff" opacity="0.42" />
              <path d="M86 22 L94 26 L94 105 L86 109 Z" fill="#020617" opacity="0.44" />
              <path d="M36 115 L82 120" stroke="#67e8f9" strokeWidth="2.2" strokeLinecap="round" opacity="0.84" />
            </g>
          </svg>
        </div>

        <div style={{ textAlign: 'center', width: 'min(560px, 92vw)', overflow: 'visible' }}>
          <div
            className="dcim-intro-title"
            style={{
              color: isDark ? 'transparent' : '#0f172a',
              background: isDark ? 'linear-gradient(90deg, #f8fafc 0%, #a5f3fc 42%, #5eead4 100%)' : 'none',
              WebkitBackgroundClip: isDark ? 'text' : 'initial',
              backgroundClip: isDark ? 'text' : 'initial',
              fontSize: 64,
              lineHeight: 1.04,
              fontWeight: 900,
              letterSpacing: '0.5px',
              textAlign: 'center',
              textShadow: isDark ? '0 18px 44px rgba(0, 0, 0, 0.46), 0 0 28px rgba(45,212,191,0.14)' : '0 18px 44px rgba(14, 116, 144, 0.18)'
            }}
          >
            DCD
          </div>
          <div
            className="dcim-intro-line"
            style={{
              width: 220,
              height: 4,
              margin: '10px auto 8px',
              borderRadius: 999,
              background: 'linear-gradient(90deg, transparent 0%, #67e8f9 18%, #2dd4bf 50%, #67e8f9 82%, transparent 100%)',
              boxShadow: '0 0 26px rgba(45, 212, 191, 0.58)'
            }}
          />
          <div
            className="dcim-intro-title"
            style={{
              color: isDark ? 'transparent' : '#0f172a',
              background: isDark ? 'linear-gradient(90deg, #f8fafc 0%, #a5f3fc 42%, #5eead4 100%)' : 'none',
              WebkitBackgroundClip: isDark ? 'text' : 'initial',
              backgroundClip: isDark ? 'text' : 'initial',
              fontSize: 54,
              lineHeight: 1.18,
              fontWeight: 850,
              letterSpacing: '-0.4px',
              textAlign: 'center',
              paddingBottom: 10,
              overflow: 'visible',
              textShadow: isDark ? '0 18px 44px rgba(0, 0, 0, 0.46), 0 0 28px rgba(45,212,191,0.14)' : '0 18px 44px rgba(14, 116, 144, 0.18)'
            }}
          >
            Digital Twin
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------
// RESPONSIVE VIEWPORT HELPER
// Keeps the twin stable at browser zoom levels 100 / 125 / 150
// and across small laptop, monitor, and fullscreen sizes.
// ---------------------------------------------------------
function useViewportSize() {
  const getSize = () => ({
    width: typeof window !== 'undefined' ? window.innerWidth : 1440,
    height: typeof window !== 'undefined' ? window.innerHeight : 900
  });

  const [size, setSize] = useState(getSize);

  useEffect(() => {
    let rafId = null;

    const update = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setSize(getSize());
        rafId = null;
      });
    };

    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    window.visualViewport?.addEventListener('resize', update);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, []);

  return size;
}

// ---------------------------------------------------------
// MAIN SCENE
// ---------------------------------------------------------
export default function DataCenterScene({ data: incomingData = null }) {
  const controlsRef = useRef();
  const twinContainerRef = useRef(null);
  const hoverClearTimerRef = useRef(null);
  const hoverFrameRef = useRef(null);
  const returnHomeTimerRef = useRef(null);
  const pendingHoverRackRef = useRef(null);
  const activeHoverRackRef = useRef(null);
  const syncInFlightRef = useRef(false);
  const lastGoodDataRef = useRef(null);
  const [isTwinFullscreen, setIsTwinFullscreen] = useState(false);
  const [topViewRequestId, setTopViewRequestId] = useState(0);
  const [normalViewRequestId, setNormalViewRequestId] = useState(0);
  const [isTopViewActive, setIsTopViewActive] = useState(false);
  const [isTopViewMode, setIsTopViewMode] = useState(false);

  const theme = 'dark';
  const isDark = true;
  const [isSidebarHidden, setIsSidebarHidden] = useState(false);

  const [sheetData, setSheetData] = useState(null);
  const [status, setStatus] = useState('Loading Digital Twin...');
  const [, setError] = useState('');

  // UI States
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredRackNum, setHoveredRackNum] = useState(null);
  const [selectedRackNum, setSelectedRackNum] = useState(null);
  const [showOverlayPopup, setShowOverlayPopup] = useState(false);
  const [isTouring, setIsTouring] = useState(false);
  const [isTourDoorOpen, setIsTourDoorOpen] = useState(false);
  const [showTwinAnalytics, setShowTwinAnalytics] = useState(false);
  const [twinMetrics, setTwinMetrics] = useState({
    fps: 0,
    frameMs: '0.0',
    dpr: 1,
    heap: 'n/a',
    load: 'warming'
  });
  const [introPhase, setIntroPhase] = useState('enter');
  const isIntroActive = introPhase !== 'done';

  const viewport = useViewportSize();
  const compactUI = viewport.width <= 1180 || viewport.height <= 760;
  const tightUI = viewport.width <= 920 || viewport.height <= 640;
  const rackPopupCompact = viewport.height <= 820 || viewport.width <= 1180;
  const rackPopupTight = viewport.height <= 680 || viewport.width <= 920;
  const edgeGap = tightUI ? 8 : compactUI ? 12 : 16;
  // Normal webapp mode can sit inside parent layouts / browser chrome, so keep
  // a small responsive bottom safety gap. Fullscreen uses the natural edge gap.
  const sidebarBottomGap = isTwinFullscreen
    ? edgeGap
    : edgeGap + Math.round(Math.min(64, Math.max(34, viewport.height * 0.052)));
  const sidebarWidth = Math.round(
    Math.max(
      tightUI ? 224 : compactUI ? 248 : 272,
      Math.min(
        tightUI ? 246 : compactUI ? 274 : 304,
        viewport.width * (tightUI ? 0.29 : compactUI ? 0.23 : 0.17)
      )
    )
  );
  const sidebarToggleSize = tightUI ? 34 : compactUI ? 38 : 42;
  const sidebarToggleLeft = isSidebarHidden
    ? edgeGap
    : edgeGap + sidebarWidth - sidebarToggleSize / 2;
  const hudWidth = Math.round(
    Math.max(228, Math.min(300, viewport.width - edgeGap * 2))
  );
  // Normal mode must respect the parent webapp container.
  // Fullscreen can use the full dynamic viewport.
  // This prevents the sidebar from running under the browser/taskbar area.
  const rootHeight = isTwinFullscreen ? '100dvh' : 'min(100dvh, 100%)';
  const rootMaxHeight = isTwinFullscreen ? '100dvh' : '100%';
  const canvasFov = tightUI ? 52 : compactUI ? 48 : 45;

  const commitHoveredRack = useCallback((rackNum) => {
    if (activeHoverRackRef.current === rackNum) return;
    activeHoverRackRef.current = rackNum;
    setHoveredRackNum(rackNum);
  }, []);

  const handleRackHover = useCallback((rackNum) => {
    if (hoverClearTimerRef.current) {
      clearTimeout(hoverClearTimerRef.current);
      hoverClearTimerRef.current = null;
    }

    if (rackNum === null) {
      pendingHoverRackRef.current = null;
      hoverClearTimerRef.current = setTimeout(() => {
        if (hoverFrameRef.current) {
          cancelAnimationFrame(hoverFrameRef.current);
          hoverFrameRef.current = null;
        }
        commitHoveredRack(null);
        hoverClearTimerRef.current = null;
      }, 140);
      return;
    }

    if (activeHoverRackRef.current === rackNum) return;

    pendingHoverRackRef.current = rackNum;

    if (hoverFrameRef.current) return;

    hoverFrameRef.current = requestAnimationFrame(() => {
      hoverFrameRef.current = null;
      if (pendingHoverRackRef.current !== null) {
        commitHoveredRack(pendingHoverRackRef.current);
      }
    });
  }, [commitHoveredRack]);

  useEffect(() => () => {
    if (hoverClearTimerRef.current) clearTimeout(hoverClearTimerRef.current);
    if (hoverFrameRef.current) cancelAnimationFrame(hoverFrameRef.current);
    if (returnHomeTimerRef.current) clearTimeout(returnHomeTimerRef.current);
  }, []);

  useEffect(() => {
    if (selectedRackNum) {
      const t = setTimeout(() => setShowOverlayPopup(true), 600);
      return () => clearTimeout(t);
    } else {
      setShowOverlayPopup(false);
    }
  }, [selectedRackNum]);

  useEffect(() => {
    let rafId;
    let frameCount = 0;
    let lastSample = typeof performance !== 'undefined' ? performance.now() : Date.now();

    const sample = (now) => {
      frameCount += 1;
      const elapsed = now - lastSample;

      if (elapsed >= 1000) {
        const fps = Math.round((frameCount * 1000) / elapsed);
        const memory = typeof performance !== 'undefined' && performance.memory
          ? `${Math.round(performance.memory.usedJSHeapSize / 1048576)} MB`
          : 'n/a';

        setTwinMetrics({
          fps,
          frameMs: fps > 0 ? (1000 / fps).toFixed(1) : '0.0',
          dpr: typeof window !== 'undefined' ? Number(window.devicePixelRatio || 1).toFixed(2) : 1,
          heap: memory,
          load: fps >= 55 ? 'low' : fps >= 42 ? 'medium' : 'high'
        });

        frameCount = 0;
        lastSample = now;
      }

      rafId = requestAnimationFrame(sample);
    };

    rafId = requestAnimationFrame(sample);

    return () => {
      cancelAnimationFrame(rafId);
    };
  }, []);

  const loadTwinData = async () => {
  /*
   * Prevent overlapping refresh requests.
   */
  if (syncInFlightRef.current) {
    return;
  }

  syncInFlightRef.current = true;

  try {
    setError('');

    setStatus(
      'Syncing S3 rack elevation and DOA data...'
    );


    // ========================================================
    // 1. LOAD RACK ELEVATION
    // ========================================================

    const parsed =
      await fetchGoogleSheetExactGrid(
        RACK_SHEET_ID,
        SHEET_GID
      );


    const rackCount =
      Array.isArray(parsed?.racks)
        ? parsed.racks.length
        : 0;


    // ========================================================
    // 2. VALIDATE DATA BEFORE USING IT
    // ========================================================

    if (
      rackCount <
      S3_TWIN_CONFIG.MIN_HEALTHY_RACK_COUNT
    ) {
      throw new Error(
        `S3 rack parser returned only ${rackCount} racks. ` +
        `Expected at least ${S3_TWIN_CONFIG.MIN_HEALTHY_RACK_COUNT}.`
      );
    }


    // ========================================================
    // 3. LOAD DOA
    // ========================================================

    let doaCsvText = '';
    let doaSyncOk = true;

    try {
      doaCsvText =
  await fetchGoogleSheetNamedCsv(
    DOA_SHEET_ID,
    DOA_REPORT_SHEET_NAME,
    DOA_REPORT_SHEET_GID
  );
    } catch (doaError) {
      doaSyncOk = false;

      console.error(
        'S3 DOA sync failed:',
        doaError
      );
    }


    // ========================================================
    // 4. BUILD DIGITAL TWIN DATA
    // ========================================================

    const doaSerialQueues =
      buildDoaSerialQueues(
        doaCsvText
      );


    const mappedData =
      mapRackElevationToTwinData(
        parsed.racks,
        doaSerialQueues
      );


    // ========================================================
    // 5. SAVE AS LAST-KNOWN-GOOD
    // ========================================================

    lastGoodDataRef.current =
      mappedData;


    setSheetData(
      mappedData
    );


    const syncedAt =
      new Date().toLocaleTimeString();


    if (doaSyncOk) {
      setStatus(
        `ACTIVE: ${rackCount} S3 racks synced at ${syncedAt}.`
      );
    } else {
      setStatus(
        `DEGRADED: Rack data synced at ${syncedAt}; DOA unavailable.`
      );
    }
  } catch (err) {
    console.error(
      'S3 Digital Twin sync error:',
      err
    );


    setError(
      err?.message ||
      'Could not load S3 Digital Twin data.'
    );


    /*
     * CRITICAL:
     *
     * Do NOT replace good rack data
     * with an empty/broken response.
     */
    if (lastGoodDataRef.current) {
      setSheetData(
        lastGoodDataRef.current
      );

      setStatus(
        `STALE: ${err?.message || 'Sync failed'}. ` +
        `Last good rack data preserved.`
      );
    } else {
      setStatus(
        `ERROR: ${err?.message || 'Could not load S3 data.'}`
      );
    }
  } finally {
    syncInFlightRef.current = false;
  }
};

  useEffect(() => {
    if (incomingData) {
      setSheetData(incomingData);
      setStatus('ACTIVE: Using provided Digital Twin data.');
      return;
    }
    loadTwinData();
    const interval = setInterval(loadTwinData, AUTO_REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [incomingData]);

  const sceneData = useMemo(() => {
    return normalizeTwinCompletion(sheetData || mapRackElevationToTwinData([]));
  }, [sheetData]);
  const failedRackNums = useMemo(
    () =>
      Object.values(sceneData)
        .filter((rack) => rack?.hasDoaFailure)
        .map((rack) => Number(rack.id)),
    [sceneData]
  );

  const rackStatusMap = useMemo(() => {
  return Object.fromEntries(
    Object.values(sceneData).map((rack) => [
      Number(rack.id),
      {
        percentage: Number(rack.percentage) || 0,
        hasDoaFailure: Boolean(rack.hasDoaFailure),
      },
    ])
  );
}, [sceneData]);

  const textPrimary = isDark ? '#f8fafc' : '#0f172a';
  const textSecondary = isDark ? '#a7c3d6' : '#64748b';
  const glassPanel = {
    background: isDark
      ? 'linear-gradient(145deg, rgba(6, 18, 30, 0.68), rgba(3, 9, 18, 0.42))'
      : 'linear-gradient(145deg, rgba(255, 255, 255, 0.46), rgba(255, 255, 255, 0.2))',
    border: `1px solid ${isDark ? 'rgba(103, 232, 249, 0.26)' : 'rgba(255, 255, 255, 0.58)'}`,
    boxShadow: isDark
      ? 'inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -22px 44px rgba(8,47,73,0.24), 0 24px 54px rgba(0,0,0,0.34), 0 0 34px rgba(34,211,238,0.08)'
      : 'inset 0 1px 0 rgba(255,255,255,0.92), inset 0 -18px 42px rgba(148,163,184,0.16), 0 18px 48px rgba(15,23,42,0.12)',
    backdropFilter: 'blur(24px) saturate(1.6)',
    WebkitBackdropFilter: 'blur(24px) saturate(1.6)'
  };
  const glassButton = {
    background: isDark
      ? 'linear-gradient(145deg, rgba(8, 145, 178, 0.24), rgba(8, 47, 73, 0.54) 48%, rgba(2, 6, 23, 0.64))'
      : 'linear-gradient(145deg, rgba(255, 255, 255, 0.7), rgba(226, 232, 240, 0.32))',
    color: textPrimary,
    border: `1px solid ${isDark ? 'rgba(125, 211, 252, 0.34)' : 'rgba(255, 255, 255, 0.7)'}`,
    boxShadow: isDark
      ? 'inset 0 1px 0 rgba(255,255,255,0.14), inset 0 -12px 24px rgba(6,182,212,0.08), 0 10px 26px rgba(0,0,0,0.3), 0 0 18px rgba(34,211,238,0.08)'
      : 'inset 0 1px 0 rgba(255,255,255,0.95), 0 8px 24px rgba(15,23,42,0.12)',
    backdropFilter: 'blur(18px) saturate(1.5)',
    WebkitBackdropFilter: 'blur(18px) saturate(1.5)'
  };

  const controlPanelIconButton = {
    ...glassButton,
    width: compactUI ? 40 : 44,
    height: compactUI ? 40 : 44,
    minWidth: compactUI ? 40 : 44,
    padding: 0,
    borderRadius: '12px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: isDark ? '#cffafe' : '#0f766e',
    background: isDark
      ? 'linear-gradient(145deg, rgba(8, 145, 178, 0.30), rgba(13, 148, 136, 0.18) 45%, rgba(2, 6, 23, 0.56))'
      : 'linear-gradient(145deg, rgba(236, 254, 255, 0.95), rgba(204, 251, 241, 0.72))',
    border: `1px solid ${isDark ? 'rgba(103, 232, 249, 0.26)' : 'rgba(20, 184, 166, 0.22)'}`,
    boxShadow: isDark
      ? 'inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -10px 24px rgba(13,148,136,0.08), 0 10px 24px rgba(0,0,0,0.24), 0 0 18px rgba(34,211,238,0.08)'
      : 'inset 0 1px 0 rgba(255,255,255,0.95), 0 8px 20px rgba(15,23,42,0.10)',
    backdropFilter: 'blur(16px) saturate(1.35)',
    WebkitBackdropFilter: 'blur(16px) saturate(1.35)',
    cursor: 'pointer',
    transition: 'all 0.2s ease'
  };

  const TopViewIcon = () => (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect x="4" y="4" width="16" height="16" rx="2.8" stroke="currentColor" strokeWidth="2" />
      <path d="M12 7v10M7 12h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M8 8h2M14 8h2M8 16h2M14 16h2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" opacity="0.75" />
    </svg>
  );

  const FullscreenIcon = ({ exit = false }) => (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {exit ? (
        <>
          <path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M9 9 5 5M15 9l4-4M9 15l-4 4M15 15l4 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" opacity="0.8" />
        </>
      ) : (
        <>
          <path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M9 4 4 9M15 4l5 5M9 20l-5-5M15 20l5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" opacity="0.8" />
        </>
      )}
    </svg>
  );


  const AutoTourIcon = ({ active = false }) => (
    <svg width="19" height="19" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      {active ? (
        <>
          <path
            d="M6 7.5C6 6.67 6.67 6 7.5 6h1C9.33 6 10 6.67 10 7.5v9C10 17.33 9.33 18 8.5 18h-1C6.67 18 6 17.33 6 16.5v-9Z"
            fill="currentColor"
          />
          <path
            d="M14 7.5c0-.83.67-1.5 1.5-1.5h1c.83 0 1.5.67 1.5 1.5v9c0 .83-.67 1.5-1.5 1.5h-1c-.83 0-1.5-.67-1.5-1.5v-9Z"
            fill="currentColor"
          />
        </>
      ) : (
        <>
          <path
            d="M8 6.8v10.4c0 .63.69 1.01 1.22.67l8.12-5.2a.8.8 0 0 0 0-1.34l-8.12-5.2A.8.8 0 0 0 8 6.8Z"
            fill="currentColor"
          />
          <path
            d="M5 19c3-1.3 4.5-4 4.5-7S8 6.3 5 5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            opacity="0.78"
          />
        </>
      )}
    </svg>
  );

  const ChevronLeftGlassIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M14.5 6.5 9 12l5.5 5.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const ChevronRightGlassIcon = () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M9.5 6.5 15 12l-5.5 5.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );

  const sidebarLiquidToggleButton = {
    width: sidebarToggleSize,
    height: sidebarToggleSize,
    minWidth: sidebarToggleSize,
    padding: 0,
    borderRadius: '999px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: textPrimary,
    cursor: 'pointer',
    userSelect: 'none',
    border: isDark
      ? '1px solid rgba(103, 232, 249, 0.42)'
      : '1px solid rgba(255, 255, 255, 0.82)',
    background: isDark
      ? 'radial-gradient(circle at 35% 25%, rgba(255,255,255,0.26), rgba(34,211,238,0.24) 35%, rgba(3,18,30,0.66) 100%)'
      : 'radial-gradient(circle at 35% 25%, rgba(255,255,255,0.92), rgba(219,234,254,0.62) 42%, rgba(148,163,184,0.24) 100%)',
    boxShadow: isDark
      ? 'inset 0 1px 0 rgba(255,255,255,0.32), inset 0 -14px 24px rgba(8,47,73,0.32), 0 14px 34px rgba(0,0,0,0.38), 0 0 30px rgba(34,211,238,0.22)'
      : 'inset 0 1px 0 rgba(255,255,255,0.95), inset 0 -14px 24px rgba(148,163,184,0.18), 0 14px 34px rgba(15,23,42,0.12), 0 0 26px rgba(14,165,233,0.16)',
    backdropFilter: 'blur(22px) saturate(1.55)',
    WebkitBackdropFilter: 'blur(22px) saturate(1.55)',
    transition: 'left 0.28s ease, transform 0.22s ease, opacity 0.22s ease'
  };


  const glassInput = {
    background: isDark
      ? 'linear-gradient(145deg, rgba(3, 18, 30, 0.68), rgba(8, 47, 73, 0.26))'
      : 'linear-gradient(145deg, rgba(255, 255, 255, 0.52), rgba(241, 245, 249, 0.28))',
    border: `1px solid ${isDark ? 'rgba(103, 232, 249, 0.24)' : 'rgba(255, 255, 255, 0.72)'}`,
    boxShadow: isDark
      ? 'inset 0 1px 0 rgba(255,255,255,0.08), inset 0 10px 24px rgba(2,6,23,0.38), 0 0 14px rgba(34,211,238,0.06)'
      : 'inset 0 1px 0 rgba(255,255,255,0.88), inset 0 10px 22px rgba(148,163,184,0.12)',
    backdropFilter: 'blur(14px) saturate(1.35)',
    WebkitBackdropFilter: 'blur(14px) saturate(1.35)'
  };

  const getRackSlots = (rackData) => {
    const slots = {};
    if (!rackData) return slots;
    Object.entries(rackData.devices).forEach(([name, dev]) => {
      dev.uPositions.forEach((uNum, idx) => {
        slots[uNum] = { 
          ...dev,
          name, 
          serialNo: dev.labels[idx] || 'Pending',
          size: dev.sizes?.[idx] || 1,
          power: dev.powerConfigs[idx] || 'N/A',
          installed: isInstalledDeviceEntry(dev, idx)
        };
      });
    });
    return slots;
  };

  const allRackNums = Array.from({ length: 52 }, (_, i) => i + 1);
  const filteredRacks = allRackNums.filter((rackNum) => {
    const rack = sceneData[rackNum];
    const rackName = String(rack?.name || '').toLowerCase();
    const displayName = String(rack?.displayName || '').toLowerCase();
    const sheetTitle = String(rack?.sheetTitle || '').toLowerCase();
    const searchClean = searchTerm.toLowerCase().replace(/[-\s/]/g, '');

    const rackNameClean = rackName.replace(/[-\s/]/g, '');
    const displayNameClean = displayName.replace(/[-\s/]/g, '');
    const sheetTitleClean = sheetTitle.replace(/[-\s/]/g, '');

    return (
      !searchClean ||
      rackNameClean.includes(searchClean) ||
      displayNameClean.includes(searchClean) ||
      sheetTitleClean.includes(searchClean) ||
      String(rackNum).includes(searchClean)
    );
  });

  const clearRackHover = useCallback(() => {
    if (hoverClearTimerRef.current) {
      clearTimeout(hoverClearTimerRef.current);
      hoverClearTimerRef.current = null;
    }
    if (hoverFrameRef.current) {
      cancelAnimationFrame(hoverFrameRef.current);
      hoverFrameRef.current = null;
    }
    pendingHoverRackRef.current = null;
    activeHoverRackRef.current = null;
    setHoveredRackNum(null);
  }, []);

  const handleRackSelect = useCallback((rackNum) => {
    setIsTouring(false);
    setIsTourDoorOpen(false);
    setIsTopViewMode(false);
    setSelectedRackNum(rackNum);
  }, []);

  const goToDefaultView = useCallback(() => {
    setIsTouring(false);
    setIsTourDoorOpen(false);
    clearRackHover();

    // Start a polished fade-out instead of instantly dropping the rack popup.
    setShowOverlayPopup(false);
    setIsTopViewMode(false);
    setIsTopViewActive(false);
    setNormalViewRequestId((prev) => prev + 1);

    if (returnHomeTimerRef.current) {
      clearTimeout(returnHomeTimerRef.current);
    }

    returnHomeTimerRef.current = setTimeout(() => {
      setSelectedRackNum(null);
      returnHomeTimerRef.current = null;
    }, 220);
  }, [clearRackHover]);

  const toggleAutoTour = useCallback(() => {
    if (isTouring) {
      goToDefaultView();
      return;
    }

    clearRackHover();
    setSelectedRackNum(null);
    setShowOverlayPopup(false);
    setIsTopViewMode(false);
    setIsTopViewActive(false);
    setIsTourDoorOpen(false);
    setIsTouring(true);
  }, [isTouring, clearRackHover, goToDefaultView]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsTwinFullscreen(document.fullscreenElement === twinContainerRef.current);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  const toggleTwinFullscreen = useCallback(async () => {
    try {
      if (!document.fullscreenElement) {
        await twinContainerRef.current?.requestFullscreen?.();
      } else {
        await document.exitFullscreen?.();
      }
    } catch (err) {
      console.error('Fullscreen toggle failed:', err);
    }
  }, []);

  const goToTopView = useCallback(() => {
    if (isTopViewMode) {
      goToDefaultView();
      return;
    }

    setIsTouring(false);
    setIsTourDoorOpen(false);
    setSelectedRackNum(null);
    clearRackHover();
    setShowOverlayPopup(false);
    setIsTopViewMode(true);
    setTopViewRequestId((prev) => prev + 1);
  }, [clearRackHover, isTopViewMode, goToDefaultView]);

  useEffect(() => {
    const handleDigitalTwinShortcuts = (event) => {
      const activeTag = document.activeElement?.tagName?.toLowerCase();

      const isTyping =
        activeTag === 'input' ||
        activeTag === 'textarea' ||
        document.activeElement?.isContentEditable;

      if (isTyping) return;
      if (event.repeat) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      const key = event.key.toLowerCase();

      if (key === 'f') {
        event.preventDefault();
        toggleTwinFullscreen();
        return;
      }

      if (key === 't') {
        event.preventDefault();
        goToTopView();
        return;
      }

      if (key === 'r') {
        event.preventDefault();
        toggleAutoTour();
      }
    };

    window.addEventListener('keydown', handleDigitalTwinShortcuts);

    return () => {
      window.removeEventListener('keydown', handleDigitalTwinShortcuts);
    };
  }, [toggleTwinFullscreen, goToTopView, toggleAutoTour]);

  const renderRackInternals = (rackNum) => {
    if (!rackNum) return null;

    const data = sceneData[rackNum];
    const slots = getRackSlots(data);
    const uSlots = Array.from({ length: 45 }, (_, i) => 45 - i);
    const failures = data?.doaFailures || [];
    const rackComponents = Object.entries(slots)
      .map(([u, device]) => {
        const topU = Math.max(1, Math.min(45, Number(u) || 1));
        // Keep the original visual rack gaps/compact device heights.
        const span = Math.max(1, Math.min(topU, getRackPopupSpan(device)));
        const bottomU = Math.max(1, topU - span + 1);

        // Sheet-based occupied U range.
        // Example: U40 with size 3 highlights U40, U41, U42.
        const sheetStartU = topU;
        const sheetSpan = Math.max(1, Math.min(45 - sheetStartU + 1, Number(device.size) || 1));
        const sheetEndU = Math.min(45, sheetStartU + sheetSpan - 1);
        const failure = failures.some((item) => {
          const serial = String(item.serialNo || '').trim().toLowerCase();
          const product = String(item.productId || '').trim().toLowerCase();
          return (
            (serial && serial === String(device.serialNo || '').trim().toLowerCase()) ||
            (product && product === String(device.name || '').trim().toLowerCase())
          );
        });

        return { topU, bottomU, span, sheetStartU, sheetEndU, sheetSpan, device, failure };
      })
      .sort((a, b) => b.topU - a.topU);

    const occupiedUSet = new Set();

    // Highlight ONLY the U numbers where the product block is actually placed in the rack view.
    // Do not highlight extra corresponding sheet-size slots.
    // If a visible product block spans U40, U41, U42, only those visible U numbers are highlighted.
    rackComponents.forEach(({ topU, bottomU }) => {
      for (let occupiedU = bottomU; occupiedU <= topU; occupiedU += 1) {
        occupiedUSet.add(occupiedU);
      }
    });

    const installedSlots = rackComponents.filter((item) => item.device.installed).length;
    const rackStatusColor =
      data?.percentage === 100 ? '#22c55e' : data?.percentage > 0 ? '#f59e0b' : '#78a9aa';

    return (
      <div style={{
        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
        background: showOverlayPopup ? 'rgba(1, 10, 18, 0.26)' : 'transparent',
        zIndex: 50, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: rackPopupTight ? '6px' : rackPopupCompact ? '10px' : '18px',
        opacity: showOverlayPopup ? 1 : 0, pointerEvents: 'none', transition: 'opacity 0.32s ease',
        backdropFilter: showOverlayPopup ? 'blur(8px) saturate(1.28)' : 'none',
        WebkitBackdropFilter: showOverlayPopup ? 'blur(8px) saturate(1.28)' : 'none'
      }}
      onClick={goToDefaultView}
      >
        <div style={{
          width: rackPopupTight ? 'min(390px, calc(100vw - 12px))' : rackPopupCompact ? 'min(430px, calc(100vw - 20px))' : 'min(500px, 72vw)',
          height: rackPopupTight ? 'calc(100% - 12px)' : rackPopupCompact ? 'calc(100% - 20px)' : 'min(820px, calc(100% - 28px))',
          maxHeight: 'calc(100% - 12px)',
          minHeight: 0,
          ...glassPanel,
          borderRadius: '22px',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.16), 0 34px 90px rgba(0,0,0,0.62), 0 0 64px rgba(34,211,238,0.14)',
          transform: showOverlayPopup ? 'scale(1) translateY(0)' : 'scale(0.96) translateY(18px)',
          transition: 'transform 0.36s cubic-bezier(0.16, 1, 0.3, 1)',
          pointerEvents: showOverlayPopup ? 'auto' : 'none',
        }}
        onClick={(event) => event.stopPropagation()}
        >
          <div style={{
            flex: '0 0 auto',
            padding: rackPopupTight ? '8px 10px 7px' : rackPopupCompact ? '10px 12px 8px' : '14px 16px 10px',
            borderBottom: '1px solid rgba(94, 234, 212, 0.2)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: rackPopupTight ? 8 : 12
          }}>
            <div>
              <h2 style={{ margin: 0, color: textPrimary, fontSize: rackPopupTight ? '13px' : rackPopupCompact ? '15px' : '18px', fontWeight: 900 }}>
                {data?.displayName || data?.name || `Rack ${rackNum}`}
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: rackPopupTight ? 6 : 10, marginTop: rackPopupTight ? 4 : 7 }}>
                <span style={{ color: '#5eead4', fontSize: rackPopupTight ? '10px' : '12px', fontWeight: 800, letterSpacing: '0.6px', textTransform: 'uppercase' }}>
                  Front rack view
                </span>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                minWidth: rackPopupTight ? 60 : 78,
                padding: rackPopupTight ? '5px 7px' : '7px 9px',
                borderRadius: rackPopupTight ? 10 : 12,
                background: 'rgba(3, 18, 30, 0.68)',
                border: '1px solid rgba(103, 232, 249, 0.22)',
                textAlign: 'right'
              }}>
                <div style={{ fontSize: 10, color: textSecondary, fontWeight: 800, textTransform: 'uppercase' }}>Assets</div>
                <div style={{ fontSize: rackPopupTight ? 12 : 15, color: textPrimary, fontWeight: 900 }}>
                  {installedSlots}/{rackComponents.length}
                </div>
              </div>
              <button
                onClick={goToDefaultView}
                title="Close rack view"
                aria-label="Close rack view"
                style={{
                  width: rackPopupTight ? 30 : 36,
                  height: rackPopupTight ? 30 : 36,
                  borderRadius: rackPopupTight ? 10 : 13,
                  cursor: 'pointer',
                  color: '#f8fafc',
                  fontSize: rackPopupTight ? 18 : 21,
                  lineHeight: rackPopupTight ? '26px' : '32px',
                  border: '1px solid rgba(248, 113, 113, 0.34)',
                  background: 'linear-gradient(145deg, rgba(127, 29, 29, 0.78), rgba(239, 68, 68, 0.48))',
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.2), 0 14px 32px rgba(239,68,68,0.22)'
                }}
              >
                x
              </button>
            </div>
          </div>

          <div style={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            padding: rackPopupTight ? '6px 16px 8px' : rackPopupCompact ? '10px 24px 12px' : '14px 38px 18px',
            display: 'flex',
            justifyContent: 'center',
            background: 'radial-gradient(circle at 50% 18%, rgba(45,212,191,0.14), transparent 42%), linear-gradient(180deg, rgba(6,28,30,0.36), rgba(1,10,12,0.56))'
          }}>
            <div style={{
              width: rackPopupTight ? 'min(330px, 100%)' : rackPopupCompact ? 'min(360px, 100%)' : 'min(390px, 100%)',
              height: '100%',
              position: 'relative',
              borderRadius: rackPopupTight ? 14 : 18,
              background: 'linear-gradient(90deg, #11252a 0%, #356066 7%, #07151a 16%, #102a2f 50%, #07151a 84%, #356066 93%, #11252a 100%)',
              border: '1px solid rgba(94, 234, 212, 0.34)',
              boxShadow: 'inset 0 0 0 3px rgba(1,10,12,0.78), inset 0 0 42px rgba(0,0,0,0.82), 0 30px 60px rgba(0,0,0,0.5), 0 0 36px rgba(45,212,191,0.12)',
              padding: rackPopupTight ? '22px 22px 26px' : rackPopupCompact ? '26px 24px 30px' : '30px 28px 34px'
            }}>
              <div style={{
                position: 'absolute',
                top: rackPopupTight ? 9 : 12,
                left: rackPopupTight ? 44 : 54,
                right: rackPopupTight ? 44 : 54,
                height: rackPopupTight ? 10 : 14,
                borderRadius: '8px 8px 2px 2px',
                background: 'linear-gradient(180deg, #79b8b5, #173b41)',
                border: '1px solid rgba(204, 251, 241, 0.22)'
              }} />
              <div style={{
                position: 'absolute',
                bottom: rackPopupTight ? 10 : 13,
                left: rackPopupTight ? 42 : 52,
                right: rackPopupTight ? 42 : 52,
                height: rackPopupTight ? 12 : 18,
                borderRadius: '2px 2px 10px 10px',
                background: 'linear-gradient(180deg, #14383d, #02090c)',
                border: '1px solid rgba(2, 20, 22, 0.92)'
              }} />

              <div style={{
                position: 'absolute',
                top: rackPopupTight ? 24 : 32,
                bottom: rackPopupTight ? 26 : 34,
                left: rackPopupTight ? 10 : 13,
                width: rackPopupTight ? 10 : 13,
                borderRadius: 8,
                background: 'repeating-linear-gradient(to bottom, #27575b 0 4px, #06151a 4px 9px)',
                boxShadow: 'inset 0 0 10px rgba(0,0,0,0.82)'
              }} />
              <div style={{
                position: 'absolute',
                top: rackPopupTight ? 24 : 32,
                bottom: rackPopupTight ? 26 : 34,
                right: rackPopupTight ? 10 : 13,
                width: rackPopupTight ? 10 : 13,
                borderRadius: 8,
                background: 'repeating-linear-gradient(to bottom, #27575b 0 4px, #06151a 4px 9px)',
                boxShadow: 'inset 0 0 10px rgba(0,0,0,0.82)'
              }} />

              <div style={{
                position: 'relative',
                height: '100%',
                display: 'grid',
                gridTemplateRows: 'repeat(45, minmax(0, 1fr))',
                borderRadius: 12,
                overflow: 'hidden',
                background: 'linear-gradient(90deg, rgba(1,10,12,0.98), rgba(10,35,39,0.9), rgba(1,10,12,0.98))',
                border: '1px solid rgba(45, 212, 191, 0.34)',
                boxShadow: 'inset 0 0 0 1px rgba(204,251,241,0.035), inset 0 0 30px rgba(0,0,0,0.72)'
              }}>
                {uSlots.map((u) => {
                  const isOccupiedU = occupiedUSet.has(u);

                  return (
                    <div
                      key={`slot-${u}`}
                      style={{
                        gridRow: `${46 - u} / span 1`,
                        position: 'relative',
                        borderTop: isOccupiedU
                          ? '1px solid rgba(94, 234, 212, 0.38)'
                          : '1px solid rgba(148, 163, 184, 0.10)',
                        background: isOccupiedU
                          ? 'linear-gradient(90deg, rgba(20, 184, 166, 0.18), rgba(34, 211, 238, 0.07), transparent)'
                          : 'transparent'
                      }}
                    >
                      <span style={{
                        position: 'absolute',
                        left: 6,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: isOccupiedU ? '#5eead4' : 'rgba(203, 213, 225, 0.34)',
                        fontSize: 8,
                        fontWeight: isOccupiedU ? 950 : 650,
                        lineHeight: 1,
                        textShadow: isOccupiedU ? '0 0 8px rgba(94, 234, 212, 0.65)' : 'none'
                      }}>
                        {u}
                      </span>
                      <span style={{
                        position: 'absolute',
                        right: 6,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        width: isOccupiedU ? 8 : 0,
                        height: isOccupiedU ? 3 : 0,
                        borderRadius: 3,
                        background: isOccupiedU
                          ? 'rgba(94, 234, 212, 0.95)'
                          : 'transparent',
                        boxShadow: isOccupiedU ? '0 0 10px rgba(94, 234, 212, 0.55)' : 'none'
                      }} />
                    </div>
                  );
                })}

                {rackComponents.map(({ topU, span, device, failure }, index) => {
                  const statusColor = failure ? '#fb7185' : device.installed ? '#22c55e' : '#78a9aa';
                  const accentColor = failure ? '#ef4444' : device.installed ? '#5eead4' : '#386064';
                  const compact = span <= 2;

                  return (
                    <div
                      key={`${device.name}-${topU}-${index}`}
                      style={{
                        gridRow: `${46 - topU} / span ${span}`,
                        margin: rackPopupTight ? '1px 22px 1px 20px' : '2px 30px 2px 28px',
                        minHeight: 0,
                        position: 'relative',
                        zIndex: 2,
                        display: 'flex',
                        alignItems: 'center',
                        gap: compact ? 6 : 10,
                        padding: rackPopupTight ? (compact ? '1px 6px' : '3px 8px') : compact ? '2px 8px' : '5px 10px',
                        borderRadius: compact ? 5 : 7,
                        color: '#f8fafc',
                        background: device.installed
                          ? 'linear-gradient(180deg, #244c52 0%, #10292f 52%, #02080a 100%)'
                          : 'linear-gradient(180deg, rgba(39,87,91,0.46) 0%, rgba(8,29,33,0.84) 100%)',
                        border: `1px solid ${failure ? 'rgba(248, 113, 113, 0.7)' : 'rgba(94, 234, 212, 0.28)'}`,
                        boxShadow: `inset 0 1px 0 rgba(255,255,255,0.12), inset 0 -10px 20px rgba(0,0,0,0.34), 0 0 ${failure ? 24 : 14}px ${failure ? 'rgba(239,68,68,0.24)' : 'rgba(45,212,191,0.1)'}`,
                        overflow: 'hidden'
                      }}
                    >
                      <div style={{
                        flex: '0 0 auto',
                        width: compact ? 28 : 42,
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: compact ? 1 : 2
                      }}>
                        {Array.from({ length: compact ? 8 : 12 }).map((_, p) => (
                          <span
                            key={p}
                            style={{
                              width: compact ? 4 : 6,
                              height: compact ? 4 : 6,
                              borderRadius: 2,
                              background: p % 5 === 0 ? accentColor : '#020a0d',
                              border: '1px solid rgba(153, 246, 228, 0.18)',
                              boxShadow: p % 5 === 0 ? `0 0 8px ${accentColor}` : 'none'
                            }}
                          />
                        ))}
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: compact ? 8.5 : 10.5,
                          fontWeight: 900,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          textShadow: '0 1px 2px rgba(0,0,0,0.8)'
                        }}>
                          {device.name}
                        </div>
                        {!compact && (
                          <div style={{
                            marginTop: 2,
                            fontSize: 8.5,
                            color: device.installed ? '#ccfbf1' : '#78a9aa',
                            fontWeight: 800,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis'
                          }}>
                            {device.installed ? `S/N ${device.serialNo}` : 'No serial assigned'}
                          </div>
                        )}
                      </div>

                      <div style={{
                        flex: '0 0 auto',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 5,
                        padding: compact ? '2px 4px' : '3px 6px',
                        borderRadius: 999,
                        background: 'rgba(1, 10, 12, 0.76)',
                        border: '1px solid rgba(94, 234, 212, 0.18)'
                      }}>
                        <span style={{
                          width: compact ? 5 : 7,
                          height: compact ? 5 : 7,
                          borderRadius: '50%',
                          background: statusColor,
                          boxShadow: `0 0 9px ${statusColor}`
                        }} />
                        {!compact && (
                          <span style={{ color: statusColor, fontSize: 8, fontWeight: 900 }}>
                            {failure ? 'FAIL' : device.installed ? 'LIVE' : 'OPEN'}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}

                <div style={{
                  position: 'absolute',
                  left: 15,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  height: '92%',
                  width: 2,
                  borderRadius: 2,
                  background: `linear-gradient(180deg, transparent, ${rackStatusColor}, transparent)`,
                  opacity: 0.72,
                  boxShadow: `0 0 18px ${rackStatusColor}`
                }} />
                <div style={{
                  position: 'absolute',
                  right: 15,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  height: '92%',
                  width: 2,
                  borderRadius: 2,
                  background: `linear-gradient(180deg, transparent, ${rackStatusColor}, transparent)`,
                  opacity: 0.72,
                  boxShadow: `0 0 18px ${rackStatusColor}`
                }} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const activeDisplayRack = hoveredRackNum || selectedRackNum;
  const activeRackData = activeDisplayRack ? sceneData[activeDisplayRack] : null;

  return (
    <div
      ref={twinContainerRef}
      className="dcim-twin-root"
      style={{
        position: 'relative',
        display: 'flex',
        width: '100%',
        height: rootHeight,
        minHeight: 0,
        maxHeight: rootMaxHeight,
        boxSizing: 'border-box',
        background: isDark
          ? 'radial-gradient(circle at 72% 18%, rgba(45,212,191,0.16), transparent 34%), radial-gradient(circle at 18% 82%, rgba(34,211,238,0.13), transparent 36%), radial-gradient(circle at 50% 50%, rgba(14,165,233,0.06), transparent 48%), #020617'
          : 'radial-gradient(circle at 72% 18%, rgba(45,212,191,0.18), transparent 34%), radial-gradient(circle at 18% 82%, rgba(34,211,238,0.14), transparent 36%), #eef8fb',
        overflow: 'hidden',
        isolation: 'isolate',
        fontFamily: '"Aptos Narrow", "Aptos", "Segoe UI Variable", "Segoe UI", system-ui, sans-serif'
      }}
    >
      <IntroAnimation isDark={isDark} onPhaseChange={setIntroPhase} />

      <style>{`
        html, body, #root {
          margin: 0;
          width: 100%;
          height: 100%;
          min-width: 0;
          min-height: 0;
          font-family: "Aptos Narrow", "Aptos", "Segoe UI Variable", "Segoe UI", system-ui, sans-serif;
        }

        body {
          overflow: hidden;
        }

        * {
          box-sizing: border-box;
          font-family: "Aptos Narrow", "Aptos", "Segoe UI Variable", "Segoe UI", system-ui, sans-serif;
        }

        .dcim-rack-scroll {
          scrollbar-width: thin;
          scrollbar-color: rgba(45, 212, 191, 0.22) transparent;
        }

        .dcim-rack-scroll::-webkit-scrollbar {
          width: 8px;
        }

        .dcim-rack-scroll::-webkit-scrollbar-track {
          background: transparent;
        }

        .dcim-rack-scroll::-webkit-scrollbar-thumb {
          background: linear-gradient(180deg, rgba(45,212,191,0.08), rgba(34,211,238,0.28), rgba(56,189,248,0.12));
          border-radius: 999px;
          border: 2px solid transparent;
          background-clip: padding-box;
        }

        .dcim-rack-scroll::-webkit-scrollbar-corner {
          background: transparent;
        }
      `}</style>

      <button
        title={isSidebarHidden ? 'Show sidebar' : 'Hide sidebar'}
        aria-label={isSidebarHidden ? 'Show sidebar' : 'Hide sidebar'}
        onClick={() => {
          if (selectedRackNum) return;
          setIsSidebarHidden((prev) => !prev);
        }}
        style={{
          ...sidebarLiquidToggleButton,
          position: 'absolute',
          top: edgeGap + (compactUI ? 94 : 108),
          left: sidebarToggleLeft,
          zIndex: selectedRackNum ? 35 : 60,
          transform: selectedRackNum
            ? 'scale(0.9)'
            : 'scale(1)',
          opacity: selectedRackNum ? 0.18 : 1,
          filter: selectedRackNum ? 'blur(8px)' : 'none',
          pointerEvents: selectedRackNum ? 'none' : 'auto',
          cursor: selectedRackNum ? 'default' : 'pointer',
          transition:
            'left 0.28s ease, transform 0.22s ease, opacity 0.22s ease, filter 0.22s ease'
        }}
      >
        {isSidebarHidden ? <ChevronRightGlassIcon /> : <ChevronLeftGlassIcon />}
      </button>

      
      
      {/* LEFT NAVIGATION PANE */}
      <div
        style={{
          position: 'absolute',
          top: edgeGap,
          left: edgeGap,
          bottom: sidebarBottomGap,
          width: sidebarWidth,
          minWidth: 0,
          maxWidth: `calc(100vw - ${edgeGap * 2}px)`,
          minHeight: 0,
          height: 'auto',
          maxHeight: `calc(100% - ${edgeGap + sidebarBottomGap}px)`,
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 25,
          overflow: 'visible',
          opacity: isSidebarHidden ? 0 : 1,
          pointerEvents: isSidebarHidden ? 'none' : 'auto',
          transform: isSidebarHidden
            ? 'translateX(-24px) scale(0.98)'
            : 'translateX(0) scale(1)',
          transition: 'opacity 0.25s ease, transform 0.25s ease'
        }}
      >
        {/* INNER SIDEBAR SHELL - keeps rounded corners visible at every zoom/display size */}
        <div
          style={{
            ...glassPanel,
            width: '100%',
            height: '100%',
            maxHeight: '100%',
            minHeight: 0,
            boxSizing: 'border-box',
            position: 'relative',
            borderRadius: compactUI ? '18px' : '22px',
            overflow: 'hidden',
            backgroundClip: 'padding-box',
            isolation: 'isolate',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* HEADER */}
          <div
            style={{
              padding: compactUI ? '12px 12px 8px' : '14px 14px 10px',
              borderBottom: `1px solid ${
                isDark
                  ? 'rgba(103, 232, 249, 0.18)'
                  : 'rgba(255, 255, 255, 0.48)'
              }`,
              flexShrink: 0
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: compactUI ? '12px' : '18px'
              }}
            >
              {/* TITLE + STATUS */}
              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  paddingRight: compactUI ? '2px' : '4px'
                }}
              >
                <h1
                  style={{
                    fontSize: compactUI ? '15px' : '18px',
                    fontWeight: 900,
                    color: textPrimary,
                    margin: 0,
                    lineHeight: 1.15,
                    letterSpacing: '-0.2px',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '9px'
                  }}
                >
                  <span>RACK INVENTORY</span>
                  <span
                    title={status.includes('ACTIVE') ? 'Live updates active' : 'Connecting'}
                    aria-label={status.includes('ACTIVE') ? 'Live updates active' : 'Connecting'}
                    style={{
                      width: 9,
                      height: 9,
                      borderRadius: '50%',
                      background: status.includes('ACTIVE') ? '#10b981' : '#ef4444',
                      boxShadow: status.includes('ACTIVE')
                        ? '0 0 14px rgba(16, 185, 129, 0.72)'
                        : '0 0 14px rgba(239, 68, 68, 0.5)',
                      flexShrink: 0
                    }}
                  />
                </h1>

              </div>

              {/* HEADER SPACER - controls are now in the bottom-right panel */}
              <div style={{ width: 0, flexShrink: 0 }} />
            </div>

            <button
              onClick={() => {
                if (isTouring) {
                  setIsTouring(false);
                  setIsTourDoorOpen(false);
                } else {
                  setSelectedRackNum(null);
                  setIsTopViewMode(false);
                  setIsTourDoorOpen(false);
                  setIsTouring(true);
                }
              }}
              style={{
                display: 'none',
                width: '100%',
                padding: '10px',
                marginTop: '16px',
                background: isTouring ? '#ef4444' : '#3b82f6',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: 800,
                cursor: 'pointer',
                boxShadow: `0 4px 12px ${
                  isTouring ? 'rgba(239, 68, 68, 0.3)' : 'rgba(59, 130, 246, 0.3)'
                }`
              }}
            >
              {isTouring ? 'STOP TOUR' : 'START AUTO-TOUR'}
            </button>

            <div style={{ position: 'relative', marginTop: compactUI ? '8px' : '10px' }}>
              <input
                type="text"
                placeholder="Search (e.g., s33a0824)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  ...glassInput,
                  width: '100%',
                  padding: compactUI ? '9px 28px 9px 10px' : '10px 30px 10px 12px',
                  color: textPrimary,
                  borderRadius: '10px',
                  fontSize: compactUI ? '12px' : '13px',
                  outline: 'none',
                  fontWeight: 600,
                  boxSizing: 'border-box'
                }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    position: 'absolute',
                    right: '8px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    color: textSecondary,
                    fontSize: '16px',
                    fontWeight: 'bold',
                    cursor: 'pointer'
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          {/* RACK LIST */}
          <div
            className="dcim-rack-scroll"
            style={{
              flex: '1 1 auto',
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: compactUI ? '8px 8px 16px' : '12px 12px 18px',
              scrollbarGutter: 'stable',
              boxSizing: 'border-box',
              overscrollBehavior: 'contain'
            }}
          >
            {filteredRacks.map((rackNum) => {
              const data = sceneData[rackNum];
              const isSelected = selectedRackNum === rackNum;
              const progress = data ? data.percentage : 0;

              let statusColor = '#64748b';
              if (progress === 100) statusColor = '#10b981';
              else if (progress > 0) statusColor = '#f59e0b';

              return (
                <div
                  key={rackNum}
                  onMouseEnter={() => handleRackHover(rackNum)}
                  onMouseLeave={() => handleRackHover(null)}
                  onClick={() => handleRackSelect(rackNum)}
                  style={{
                    padding: compactUI ? '7px 7px' : '8px 8px',
                    marginBottom: compactUI ? '4px' : '5px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    background: isSelected
                      ? isDark
                        ? 'linear-gradient(145deg, rgba(20,184,166,0.2), rgba(34,211,238,0.08))'
                        : 'rgba(255, 255, 255, 0.44)'
                      : 'transparent',
                    border: `1px solid ${
                      isSelected
                        ? isDark
                          ? 'rgba(45, 212, 191, 0.42)'
                          : 'rgba(14, 132, 199, 0.26)'
                        : 'transparent'
                    }`,
                    boxShadow: isSelected
                      ? isDark
                        ? 'inset 0 1px 0 rgba(255,255,255,0.1), 0 8px 24px rgba(0,0,0,0.24), 0 0 18px rgba(45,212,191,0.1)'
                        : 'inset 0 1px 0 rgba(255,255,255,0.8), 0 8px 20px rgba(15,23,42,0.08)'
                      : 'none',
                    backdropFilter: isSelected ? 'blur(14px) saturate(1.25)' : 'none',
                    transition: 'all 0.2s ease',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: '8px',
                      marginBottom: compactUI ? '5px' : '6px'
                    }}
                  >
                    <span
                      style={{
                        fontWeight: 700,
                        color: isSelected
                          ? isDark
                            ? '#67e8f9'
                            : '#0284c7'
                          : textPrimary,
                        fontSize: compactUI ? '12px' : '13px',
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {data?.displayName || data?.name || `Rack ${rackNum}`}
                    </span>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: compactUI ? '4px' : '6px',
                        fontSize: compactUI ? '11px' : '12px',
                        fontWeight: 800,
                        color: statusColor,
                        flexShrink: 0
                      }}
                    >
                      {progress}%
                    </span>
                  </div>
                  <div
                    style={{
                      display: 'block',
                      width: '100%',
                      height: compactUI ? '3px' : '4px',
                      background: isDark ? '#06111f' : '#cbd5e1',
                      borderRadius: '999px',
                      overflow: 'hidden'
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${progress}%`,
                        background: statusColor,
                        borderRadius: '999px'
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </div>

      {/* 3D CANVAS AREA */}
      <div style={{ flex: 1, position: 'relative', minWidth: 0, minHeight: 0, height: '100%', overflow: 'hidden' }}>
        {/* FIXED TOP RIGHT HUD */}
        <div style={{
          position: 'absolute', top: edgeGap, right: edgeGap, zIndex: 20,
          width: hudWidth, maxWidth: `calc(100vw - ${edgeGap * 2}px)`, display: 'flex', flexDirection: 'column', gap: compactUI ? '8px' : '12px',
          pointerEvents: showOverlayPopup ? 'none' : 'auto',
          transition: 'opacity 0.3s ease',
          opacity: showOverlayPopup ? 0 : 1
        }}>
        <div style={{
          ...glassPanel,
          borderRadius: '14px', padding: compactUI ? '12px 14px' : '16px 20px',
          pointerEvents: 'none'
        }}>
          <h3 style={{ margin: '0 0 12px 0', fontSize: '12px', color: textSecondary, textTransform: 'uppercase', letterSpacing: '1px' }}>
            Server Rack Status
          </h3>
          {activeDisplayRack ? (
            <div>
              <div style={{ fontSize: compactUI ? '12px' : '13px', fontWeight: 800, color: textPrimary, marginBottom: '4px', lineHeight: 1.25 }}>
                {sceneData?.[activeDisplayRack]?.displayName || sceneData?.[activeDisplayRack]?.name || `Rack ${activeDisplayRack}`}
              </div>
              <div style={{ fontSize: '13px', color: textSecondary, marginBottom: '16px' }}>
                Zone: {activeRackData?.su || 'Unknown'}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end' }}>
                <div>
                  <div style={{ fontSize: '11px', color: textSecondary, marginBottom: '2px' }}>COMPLETION</div>
                  <div style={{ fontSize: '24px', fontWeight: 900, color: activeRackData?.percentage === 100 ? '#10b981' : (activeRackData?.percentage > 0 ? '#f59e0b' : '#ef4444') }}>
                    {activeRackData?.percentage || 0}%
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '11px', color: textSecondary, marginBottom: '2px' }}>ASSETS</div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: textPrimary }}>
                    {activeRackData?.inspected || 0} / {activeRackData?.total || 0}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ color: textSecondary, fontSize: '13px', fontStyle: 'italic' }}>
              Hover or select a rack to view status.
            </div>
          )}
        </div>
        </div>


        {/* HIDDEN TWIN ANALYTICS TOGGLE */}
        <div
          style={{
            position: 'absolute',
            left: isSidebarHidden ? edgeGap + 8 : edgeGap + 14,
            bottom: sidebarBottomGap + 14,
            zIndex: 80,
            pointerEvents: showOverlayPopup || isSidebarHidden ? 'none' : 'auto'
          }}
        >
          <button
            title="Twin analytics"
            aria-label="Twin analytics"
            onClick={() => setShowTwinAnalytics((prev) => !prev)}
            style={{
              width: 9,
              height: 9,
              minWidth: 9,
              padding: 0,
              borderRadius: '999px',
              border: '1px solid rgba(94, 234, 212, 0.18)',
              background: showTwinAnalytics
                ? 'rgba(45, 212, 191, 0.58)'
                : 'rgba(45, 212, 191, 0.10)',
              boxShadow: showTwinAnalytics
                ? '0 0 12px rgba(45, 212, 191, 0.34)'
                : 'none',
              opacity: showTwinAnalytics ? 0.9 : 0.18,
              cursor: 'pointer'
            }}
          />

          {showTwinAnalytics && (
            <div
              style={{
                position: 'absolute',
                left: 0,
                bottom: 16,
                width: 172,
                padding: '10px 11px',
                borderRadius: 14,
                background: 'linear-gradient(145deg, rgba(3, 18, 30, 0.76), rgba(8, 47, 73, 0.42))',
                border: '1px solid rgba(94, 234, 212, 0.22)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 18px 38px rgba(0,0,0,0.28)',
                backdropFilter: 'blur(16px) saturate(1.25)',
                WebkitBackdropFilter: 'blur(16px) saturate(1.25)',
                color: textPrimary,
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.1px'
              }}
            >
              <div style={{ fontSize: 10, color: '#5eead4', fontWeight: 900, textTransform: 'uppercase', marginBottom: 7 }}>
                Twin Runtime
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '5px 10px' }}>
                <span style={{ color: textSecondary }}>FPS</span>
                <span>{twinMetrics.fps}</span>
                <span style={{ color: textSecondary }}>Frame</span>
                <span>{twinMetrics.frameMs} ms</span>
                <span style={{ color: textSecondary }}>DPR</span>
                <span>{twinMetrics.dpr}</span>
                <span style={{ color: textSecondary }}>Heap</span>
                <span>{twinMetrics.heap}</span>
                <span style={{ color: textSecondary }}>Load</span>
                <span style={{ color: twinMetrics.load === 'high' ? '#fb7185' : twinMetrics.load === 'medium' ? '#f59e0b' : '#5eead4' }}>
                  {twinMetrics.load}
                </span>
              </div>
            </div>
          )}
        </div>


        {/* BOTTOM RIGHT CONTROL PANEL */}
        <div
          style={{
            position: 'absolute',
            right: edgeGap,
            bottom: edgeGap,
            zIndex: 24,
            display: 'flex',
            alignItems: 'center',
            gap: compactUI ? '8px' : '10px',
            padding: compactUI ? '10px 12px' : '12px 14px',
            borderRadius: compactUI ? '16px' : '18px',
            background: isDark
              ? 'linear-gradient(145deg, rgba(8, 47, 73, 0.46), rgba(15, 23, 42, 0.28))'
              : 'rgba(255,255,255,0.34)',
            border: `1px solid ${isDark ? 'rgba(45, 212, 191, 0.22)' : 'rgba(20, 184, 166, 0.18)'}`,
            boxShadow: isDark
              ? 'inset 0 1px 0 rgba(255,255,255,0.08), 0 14px 30px rgba(0,0,0,0.22)'
              : 'inset 0 1px 0 rgba(255,255,255,0.78), 0 14px 28px rgba(15,23,42,0.08)',
            backdropFilter: 'blur(16px) saturate(1.25)',
            WebkitBackdropFilter: 'blur(16px) saturate(1.25)',
            pointerEvents: showOverlayPopup ? 'none' : 'auto',
            opacity: showOverlayPopup ? 0 : 1,
            transition: 'opacity 0.25s ease'
          }}
        >
          <button
            title={isTopViewMode ? 'Exit top view' : 'Top view'}
            aria-label={isTopViewMode ? 'Exit top view' : 'Top view'}
            onClick={goToTopView}
            style={{
              ...controlPanelIconButton,
              color: isTopViewMode ? '#ffffff' : controlPanelIconButton.color,
              background: isTopViewMode
                ? 'linear-gradient(145deg, rgba(20, 184, 166, 0.58), rgba(34, 211, 238, 0.34))'
                : controlPanelIconButton.background,
              border: isTopViewMode
                ? '1px solid rgba(94, 234, 212, 0.42)'
                : controlPanelIconButton.border,
              boxShadow: isTopViewMode
                ? 'inset 0 1px 0 rgba(255,255,255,0.22), 0 0 26px rgba(45,212,191,0.28)'
                : controlPanelIconButton.boxShadow
            }}
          >
            <TopViewIcon />
          </button>

          <button
            title={isTwinFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            aria-label={isTwinFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            onClick={toggleTwinFullscreen}
            style={controlPanelIconButton}
          >
            <FullscreenIcon exit={isTwinFullscreen} />
          </button>

          <button
            title={isTouring ? 'Stop auto tour' : 'Start auto tour'}
            aria-label={isTouring ? 'Stop auto tour' : 'Start auto tour'}
            onClick={toggleAutoTour}
            style={{
              ...controlPanelIconButton,
              color: isTouring ? '#fecaca' : isDark ? '#ccfbf1' : '#0f766e',
              background: isTouring
                ? 'linear-gradient(145deg, rgba(239, 68, 68, 0.52), rgba(127, 29, 29, 0.44))'
                : controlPanelIconButton.background,
              border: isTouring
                ? '1px solid rgba(248, 113, 113, 0.34)'
                : controlPanelIconButton.border
            }}
          >
            <AutoTourIcon active={isTouring} />
          </button>
        </div>

        {/* 3D RENDERER */}
        <Canvas
          camera={{ position: [0, 45, 60], fov: canvasFov, near: 0.1, far: 500 }}
          dpr={[1, 1.5]}
          performance={{ min: 0.82 }}
          resize={{ scroll: false, debounce: { scroll: 50, resize: 0 } }}
          gl={{
            antialias: true,
            alpha: false,
            stencil: false,
            depth: true,
            powerPreference: 'high-performance'
          }}
          style={{ width: '100%', height: '100%', display: 'block', touchAction: 'none', willChange: 'transform' }}
        >
          <AdaptiveDpr pixelated={false} />
          <color attach="background" args={[isDark ? '#092235' : '#e8f0f7']} />
<fog attach="fog" args={[isDark ? '#092235' : '#e8f0f7', 14, 165]} />

<ambientLight
  intensity={isDark ? 0.48 : 0.78}
  color={isDark ? '#d7fbff' : '#ffffff'}
/>

<directionalLight
  position={[20, 40, 20]}
  intensity={isDark ? 1.48 : 1.15}
  color={isDark ? '#effdff' : '#ffffff'}
/>

<directionalLight
  position={[-28, 22, 14]}
  intensity={isDark ? 0.38 : 0.18}
  color={isDark ? '#67e8f9' : '#dbeafe'}
/>

<mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, -0.055, 0]}>
  <planeGeometry args={[100, 100]} />
  <meshStandardMaterial
    color={isDark ? '#4f737c' : '#d7eef2'}
    roughness={0.46}
    metalness={0.12}
    emissive={isDark ? '#12313a' : '#c7eef3'}
    emissiveIntensity={isDark ? 0.08 : 0.03}
  />
</mesh>

<ScalableUnits
  theme={theme}
  isTouring={isTourDoorOpen}
  failedRackNums={failedRackNums}
  rackStatusMap={rackStatusMap}
  onRackHover={handleRackHover}
  onRackClick={handleRackSelect}
/>

          <TourAnimator
            controlsRef={controlsRef}
            isTouring={isTouring}
            onDoorReady={setIsTourDoorOpen}
          />

          <TopViewAnimator
            controlsRef={controlsRef}
            requestId={topViewRequestId}
            isBlocked={isTouring || isIntroActive}
            onStart={() => setIsTopViewActive(true)}
            onDone={() => setIsTopViewActive(false)}
          />

          <CameraAnimator
            controlsRef={controlsRef}
            selectedRackNum={selectedRackNum}
            isTouring={isTouring || isIntroActive || isTopViewActive}
            suppressDefaultCamera={isTopViewMode}
            normalViewRequestId={normalViewRequestId}
          />

          <DroneControls
            controlsRef={controlsRef}
            isTouring={isTouring}
            isLocked={!!selectedRackNum || isIntroActive || isTopViewActive || isTopViewMode}
          />

          <CameraBounds
            controlsRef={controlsRef}
            isTouring={isTouring || isIntroActive || isTopViewActive}
          />

          <IntroCameraAnimator
            controlsRef={controlsRef}
            introPhase={introPhase}
          />

          {/* Angle-limited orbital controls: broad page movement, no under-floor or extreme top/side views */}
          <OrbitControls
            ref={controlsRef}
            makeDefault
            enabled={!isTouring && !isIntroActive && !isTopViewActive && !isTopViewMode}
            target={[0, 0, 0]}
            minPolarAngle={0}
            maxPolarAngle={THREE.MathUtils.degToRad(89.95)}
            minAzimuthAngle={
              isTouring || isIntroActive || isTopViewActive
                ? -Math.PI
                : THREE.MathUtils.degToRad(-85)
            }
            maxAzimuthAngle={
              isTouring || isIntroActive || isTopViewActive
                ? Math.PI
                : THREE.MathUtils.degToRad(85)
            }
            minDistance={0.35}
            maxDistance={125}
            rotateSpeed={0.55}
            panSpeed={0.65}
            zoomSpeed={0.28}
            enablePan={!isTouring && !isIntroActive && !isTopViewActive && !isTopViewMode}
            enableRotate={!isTouring && !isIntroActive && !isTopViewActive && !isTopViewMode}
            enableZoom={!isTouring && !isIntroActive && !isTopViewActive && !isTopViewMode}
            screenSpacePanning
            enableDamping={!isTouring && !isIntroActive && !isTopViewActive && !isTopViewMode}
            dampingFactor={0.05}
          />
        </Canvas>

        {/* FIXED RACK INTERNAL POPUP */}
        {renderRackInternals(selectedRackNum)}

      </div>
    </div>
  );
}